/**
🔗 TESTER CETTE PAGE : http://dev.sellerie.local/devis/configurateur/devis-1761200573138
📝 Ctrl+Click sur le lien ci-dessus pour ouvrir un devis existant dans le navigateur

SELLERIE MARINE - COMPOSANT CONFIGURATEUR ARTICLE

📋 DESCRIPTION:
Composant React principal du configurateur d'articles pour la gestion complète des devis.
Interface unifiée pour création, modification et finalisation des devis sellerie marine.

🎯 RESPONSABILITÉS:
- Configuration complète des articles (dimensions, matériaux, options, prix)
- Gestion du cycle de vie des devis (création, modification, finalisation)
- Optimisation automatique du calepinage avec économies calculées
- Interface de remises commerciales (détaillées et globales)
- Finalisation avec conditions commerciales et génération PDF
- Alerte automatique de mise à jour des tarifs

🔗 DÉPENDANCES:
- React Router: Navigation et paramètres d'URL
- Services API: Calculs de prix, sauvegarde, optimisation
- Composants spécialisés: Configuration, détails, finalisation
- Hooks personnalisés: Alerte tarifs, abonnement premium

📡 INTERACTIONS:
- Backend: Calculs de prix, optimisation, sauvegarde devis
- Services: Matières, clients, tarifs, export PDF
- Navigation: Retour liste devis, sélection clients
- Stockage: Sauvegarde automatique, état persistant

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Sauvegarde automatique toutes les 2 secondes en mode édition
- Alerte tarifs avec timestamps complets pour éviter faux positifs
- Optimisation calepinage réservée aux abonnements premium
- Interface adaptative selon mode création/édition
- Gestion complète des remises avec calculs en temps réel
- Finalisation avec validation des champs obligatoires
*/
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTarifsAlert } from '../../../hooks/useTarifsAlert';
import TarifsAlert from '../../../components/Devis/TarifsAlert';
import TvaStatusAlert from '../../../components/Devis/TvaStatusAlert';
import { hasSeededCriticalEntreprise, getCriticalEntrepriseFieldsStatus } from '../../../utils/seededEntreprise';
import { BandeauModeConserve } from '../../../components/Devis/BandeauModeConserve';
import { Article, Matiere, CalculPrix } from './types';
import { subscriptionService } from '../../../services/SubscriptionService';
import { Devis } from '../../../services/types';
import { apiService } from '../../../services/ApiService';
import { clientService } from '../../../services/ClientService';
import { devisApiService } from '../../../services/DevisApiService';
import { matiereService } from '../../../services/MatiereService';
import ArticleConfigurationCompact from './ArticleConfigurationCompact';
import Materiaux from './Materiaux';
import DetailsPrix from './DetailsPrix';
import ArticleLibreModal from './ArticleLibreModal';
import { ExportButtons } from '../../../components/Export';
import AuthContext from '../../../contexts/AuthContext';
import { ExportService } from '../../../services/ExportService';
import DetailPieces from './components/DetailPieces';
import DetailCouts from './components/DetailCouts';
import OptimisationCompact from './components/OptimisationCompact';
import OptimisationModal from './components/OptimisationModal';
import FinalisationCompact from './components/FinalisationCompact';
import FinalisationModal, { FinalisationData } from './components/FinalisationModal';
import DevisAttachmentsSection from '../../../components/Devis/DevisAttachmentsSection';
import { apiFetch } from '../../../services/httpClient';

// Composant pour les boutons d'action
interface ActionButtonsProps {
  calculating: boolean;
  prixCalcule: CalculPrix | null;
  donneesModifiees: boolean;
  isEditingArticle: boolean;
  error: string | null;
  onReinitialiser: () => void;
  onCalculerPrix: () => void;
  onAjouterAuDevis: () => void;
  onMettreAJourArticle: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  calculating,
  prixCalcule,
  donneesModifiees,
  isEditingArticle,
  error,
  onReinitialiser,
  onCalculerPrix,
  onAjouterAuDevis,
  onMettreAJourArticle
}) => {
  const hasPrix = !!(prixCalcule && !donneesModifiees);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-wrap gap-4 justify-center">
        <button type="button" className="btn-secondary" onClick={onReinitialiser}>
          Réinitialiser
        </button>
        
        <button
          type="button"
          className={hasPrix ? 'btn-secondary' : 'btn-primary'}
          onClick={onCalculerPrix}
          disabled={calculating}
        >
          {calculating ? 'Calcul en cours...' : hasPrix ? 'Recalculer le prix' : 'Calculer le prix'}
        </button>
        
        {hasPrix && (
          <button
            type="button"
            className="btn-success"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isEditingArticle) {
                onMettreAJourArticle();
              } else {
                onAjouterAuDevis();
              }
            }}
          >
            {isEditingArticle ? 'Mettre à jour l\'article' : 'Ajouter au devis'}
          </button>
        )}
      </div>
    </div>
  );
};

const ConfigurateurArticle: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { access, startTrial } = useContext(AuthContext);
  
  // Récupérer le clientId depuis l'URL ou l'état de location
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clientId = searchParams.get('client') || location.state?.clientId || '';
  const articlesDevisFromLocation = location.state?.articlesDevis || [];
  
  // Vérifier si on est en mode édition
  const isEditMode = id !== undefined;
  const [devisExistant, setDevisExistant] = useState<any>(null);
  
  // États principaux
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [prixCalcule, setPrixCalcule] = useState<CalculPrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDetailsPrix, setShowDetailsPrix] = useState(false);
  const [articlesDevis, setArticlesDevis] = useState<Article[]>(articlesDevisFromLocation);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
  const [entrepriseData, setEntrepriseData] = useState<any>(null);
  const [showParamsPanel, setShowParamsPanel] = useState(false);
  const [inclureCgv, setInclureCgv] = useState<boolean>(false);
  
  // États pour la gestion du mode d'édition d'article
  const [isEditingArticle, setIsEditingArticle] = useState<boolean>(false);
  const [showArticleModal, setShowArticleModal] = useState<boolean>(false);
  const [showArticleLibreModal, setShowArticleLibreModal] = useState<boolean>(false);
  const [articleLibreEnEdition, setArticleLibreEnEdition] = useState<any | null>(null);
  const [showDetailsPrixModal, setShowDetailsPrixModal] = useState<boolean>(false);
  const [articleDetailsPrix, setArticleDetailsPrix] = useState<Article | null>(null);
  const [showDetailPiecesModal, setShowDetailPiecesModal] = useState<boolean>(false);
  const [showDetailCoutsModal, setShowDetailCoutsModal] = useState<boolean>(false);
  const [showOptimisationModal, setShowOptimisationModal] = useState<boolean>(false);
  const [showFinalisationModal, setShowFinalisationModal] = useState<boolean>(false);
  const [finalisationMode, setFinalisationMode] = useState<'full' | 'fees'>('full');
  
  // États pour la finalisation du devis
  const [finalisationData, setFinalisationData] = useState<FinalisationData>({});
  const [notesInternes, setNotesInternes] = useState<string>('');
  const [notesMessage, setNotesMessage] = useState<string | null>(null);
  const [notesSaving, setNotesSaving] = useState<boolean>(false);
  
  // État pour le nombre de pièces calculé par le composant DetailPieces
  const [nombrePiecesCalculees, setNombrePiecesCalculees] = useState<number>(0);
  
  // États pour les remises
  const [remisesAppliquees, setRemisesAppliquees] = useState<{
    mainOeuvre: number;
    matieresFournitures: number;
    globale?: number;
    typeRemise?: 'detaillee' | 'globale';
  }>({ mainOeuvre: 0, matieresFournitures: 0, typeRemise: 'detaillee' });

  // Calculer automatiquement le nombre de pièces au chargement
  useEffect(() => {
    const calculerNombrePieces = async () => {
      if (articlesDevis.length > 0 && matieres.length > 0) {
        try {
          const result = await apiService.genererDetailPieces(articlesDevis, matieres);
          if (result.success && result.pieces) {
            setNombrePiecesCalculees(result.pieces.length);
          }
        } catch (error) {
          console.error('Erreur lors du calcul automatique des pièces:', error);
          // En cas d'erreur, garder la valeur par défaut
        }
      } else {
        setNombrePiecesCalculees(0);
      }
    };

    calculerNombrePieces();
  }, [articlesDevis, matieres]);
  
  // États pour les fonctionnalités premium
  const [optimisationActive, setOptimisationActive] = useState(false);
  const [devisOptimise, setDevisOptimise] = useState<any | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [hasPremiumFeatures, setHasPremiumFeatures] = useState(false);
  const [donneesModifiees, setDonneesModifiees] = useState(false);
  const [integrerEconomieCalepinage, setIntegrerEconomieCalepinage] = useState(false);
  const [justInvalidated, setJustInvalidated] = useState(false); // ✅ NOUVEAU: Tracker invalidation automatique
  
  // État pour détecter les modifications du devis
  const [devisModifie, setDevisModifie] = useState(false);
  const [devisInitial, setDevisInitial] = useState<any>(null);
  
  // État pour le mode conservé (temporaire, en mémoire uniquement)
  const [modeConserve, setModeConserve] = useState<boolean>(false);
  
  // États pour l'alerte de tarifs
  const [showTarifsAlert, setShowTarifsAlert] = useState(false);
  const [tarifsAlertProcessed, setTarifsAlertProcessed] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // Clé pour forcer le re-render
  
  // État par défaut de l'article
  const [article, setArticle] = useState<Article>({
    nom: '',
    type: 'coussin',
    forme: '', // Aucune forme par défaut
    dimensions: {
      longueur: 50,
      largeur: 30,
      hauteur: 10,
      talon: 1,
    },
    options: {
      fond: true,
      grille: false,
      talonAgrafageActif: false,
      talonAgrafage: 3,
      mousse: 'mousse_003', // Utiliser l'ID de la mousse 30A/8 MOUSSE STANDARD 30 A
      fermeture: 'glissiere',
      surpiqure: false,
      formeComplexe: false,
    },
    materiaux: {
      dessus: { matiere: '', couleur: '' },
      dessous: { matiere: '', couleur: '' },
      cotes: { matiere: '', couleur: '' },
    },
    quantite: 1,
    prixUnitaire: 0,
  });

  // Vérifier abonnement premium
  useEffect(() => {
    const checkSubscription = () => {
      setHasPremiumFeatures(subscriptionService.hasPremiumFeatures());
    };
    checkSubscription();
    const intervalId = setInterval(checkSubscription, 5000);
    return () => clearInterval(intervalId);
  }, []);
  
  // Charger les matières
  useEffect(() => {
    const fetchMatieres = async () => {
      try {
        setLoading(true);
        const matieresFromService = await matiereService.getMatieres();
        setMatieres(matieresFromService);
      } catch (error) {
        console.error("Erreur lors du chargement des matières:", error);
        const storedMatieres = localStorage.getItem('matieres');
        if (storedMatieres) {
          try {
            setMatieres(JSON.parse(storedMatieres));
          } catch (parseError) {
            console.error("Erreur parsing localStorage:", parseError);
          }
        }
      } finally {
        setLoading(false);
        // Afficher le formulaire uniquement s'il n'y a pas d'articles ET qu'on n'est pas en mode édition
        if (articlesDevisFromLocation.length === 0 && !isEditMode) {
          setShowArticleModal(true);
        }
      }
    };
    fetchMatieres();
  }, []); // Supprimer la dépendance à articlesDevis.length
  
  // Charger le devis existant en mode édition
  useEffect(() => {
    const fetchDevis = async () => {
      if (isEditMode && !loading) {
        try {
          const devisData = await devisApiService.getDevisById(id!);
          setDevisExistant(devisData);
          
          if (devisData.articles?.length > 0) {
            setArticlesDevis(devisData.articles);
          }
          
          // ✅ CORRECTION : Simplement RESTAURER les données, NE PAS recalculer
          if ((devisData as any).optimisationActive !== undefined) {
            setOptimisationActive((devisData as any).optimisationActive);
            console.log('✅ [CHARGEMENT] optimisationActive restauré:', (devisData as any).optimisationActive);
          }
          if ((devisData as any).devisOptimise) {
            setDevisOptimise((devisData as any).devisOptimise);
            console.log('✅ [CHARGEMENT] devisOptimise restauré:', {
              economieTotal: (devisData as any).devisOptimise?.economieTotal,
              hasPreferencesEstimation: !!(devisData as any).devisOptimise?.preferences_estimation,
              k: (devisData as any).devisOptimise?.preferences_estimation?.k,
              hasGroupesMatiere: !!(devisData as any).devisOptimise?.groupesMatiere,
              devisOptimiseComplete: (devisData as any).devisOptimise
            });
          } else {
            console.log('⚠️ [CHARGEMENT] Pas de devisOptimise dans le devis chargé');
          }
          
          // Restaurer le flag integrerEconomieCalepinage depuis meta
          if ((devisData as any).meta?.integrerEconomieCalepinage !== undefined) {
            setIntegrerEconomieCalepinage((devisData as any).meta.integrerEconomieCalepinage);
            console.log('✅ [CHARGEMENT] integrerEconomieCalepinage restauré:', (devisData as any).meta.integrerEconomieCalepinage);
          }
          
          // Restaurer les données de finalisation si elles étaient sauvegardées
          if ((devisData as any).finalisationData) {
            setFinalisationData((devisData as any).finalisationData);
          }
          
          // ✅ CORRECTION: Restaurer les remises si elles étaient sauvegardées
          if ((devisData as any).remises) {
            setRemisesAppliquees((devisData as any).remises);
          }
          
          // Sauvegarder l'état initial du devis pour détecter les modifications
          setDevisInitial({
            articles: devisData.articles || [],
            optimisationActive: (devisData as any).optimisationActive || false,
            devisOptimise: (devisData as any).devisOptimise || null,
            finalisationData: (devisData as any).finalisationData || {},
            remises: (devisData as any).remises || { mainOeuvre: 0, matieresFournitures: 0, typeRemise: 'detaillee' }
          });
          setDevisModifie(false);
          
          // Réinitialiser l'état de l'alerte de tarifs
          setTarifsAlertProcessed(false);
          
          if (devisData.client) {
            const client = await clientService.getClient(devisData.client);
            setClientInfo(client);
          }
        } catch (error) {
          console.error("Erreur lors du chargement du devis:", error);
          setError("Impossible de charger le devis demandé");
          navigate('/devis');
        }
      }
    };
    fetchDevis();
  }, [id, isEditMode, loading, navigate]);
  
  // Charger les informations du client
  useEffect(() => {
    const fetchClient = async () => {
      if (clientId) {
        try {
          const client = await clientService.getClient(clientId);
          setClientInfo(client);
        } catch (error) {
          console.error("Erreur lors du chargement du client:", error);
        }
      }
    };
    fetchClient();
  }, [clientId]);

  // Charger les données de l'entreprise (pour TVA)
  useEffect(() => {
    const fetchEntreprise = async () => {
      try {
        const data = await apiFetch<any>('/entreprise');
        console.log('🔍 [DIAGNOSTIC] Données entreprise reçues:', {
          nom: data?.nom,
          soumisATVA: data?.soumisATVA,
          tauxTVA: data?.tauxTVA,
          seeded_fields: data?.seeded_fields,
          fullData: data
        });
        setEntrepriseData(data);
        // Réinitialiser la sélection CGV si l'entreprise n'a pas de CGV
        if (!data?.has_cgv) {
          setInclureCgv(false);
        }
      } catch (error) {
        console.error("🔍 [DIAGNOSTIC] Erreur lors du chargement des données de l'entreprise:", error);
      }
    };
    fetchEntreprise();
  }, []);

  // Synchroniser les notes internes lorsque le devis est chargÃ© ou rechargÃ©
  useEffect(() => {
    if (devisExistant) {
      const notes = (devisExistant as any).notes_internes || (devisExistant as any).notesInternes || '';
      setNotesInternes(notes || '');
    }
  }, [devisExistant]);

  // Sauvegarde automatique à chaque modification (optimisée)
  useEffect(() => {
    const sauvegarderAutomatiquement = async () => {
      if (!isEditMode || !devisExistant || !devisModifie || modeConserve) return;
      if (isOptimizing) return;

      try {
        // Récupérer le clientId depuis les données de finalisation ou l'état actuel
        const clientIdToSave = finalisationData.clientId || clientId || devisExistant.client;
        
        // Calculer le statut automatiquement
        const nouveauStatut = calculerStatutDevis();
        
        const economieCalculee = devisOptimise?.economieTotal ? Math.abs(Number(devisOptimise.economieTotal)) : 0;

        const devisAEnregistrer = {
          ...devisExistant,
          client: clientIdToSave, // Mettre à jour le client du devis
          projet: finalisationData.nomProjet || devisExistant.projet, // ✅ CORRECTION: Mettre à jour le projet depuis finalisationData
          articles: articlesDevis,
          montantTotal: calculerTotal(),
          optimisation_active: optimisationActive, // ✅ CORRECTION: snake_case pour le backend
          economie_optimisation: economieCalculee, // ✅ CORRECTION: Utiliser la variable calculée
          devisOptimise: devisOptimise,
          remises: calculerMontantsRemisesPourPDF(), // ✅ CORRECTION: Inclure les remises avec montants calculés
          meta: { // ✅ CORRECTION: Inclure meta avec integrerEconomieCalepinage
            integrerEconomieCalepinage: integrerEconomieCalepinage
          },
          // finalisationData supprimé - géré directement par la modal
          statut: nouveauStatut
        } as any;

        // Log pour diagnostiquer ce qui est envoyé au backend
        console.log('🔍 [Sauvegarde BDD] Données envoyées:', {
          devisId: devisExistant.id,
          hasDevisOptimise: !!devisOptimise,
          devisOptimise_k: devisOptimise?.k,
          devisOptimise_keys: devisOptimise ? Object.keys(devisOptimise) : [],
          devisOptimiseComplet: JSON.stringify(devisOptimise)
        });

        const resultatSauvegarde = await devisApiService.saveDevis(devisAEnregistrer);
        
        // Log de confirmation de sauvegarde avec K
        const kSauvegarde = devisOptimise?.k || devisOptimise?.preferences_estimation?.k;
        if (kSauvegarde) {
          console.log(`🟢 [K] Sauvegarde BDD confirmée: K=${kSauvegarde} (devisId: ${devisExistant.id})`);
        }
        
        // Mettre à jour l'état local du devis existant
        setDevisExistant((prev: any) => ({ 
          ...prev, 
          statut: nouveauStatut,
          client: clientIdToSave 
        }));
        
        // Réinitialiser le flag de modification après sauvegarde
        setDevisModifie(false);
        
        // Mettre à jour les informations du client si nécessaire
        if (clientIdToSave && clientIdToSave !== clientId) {
          try {
            const client = await clientService.getClient(clientIdToSave);
            setClientInfo(client);
          } catch (error) {
            console.error("Erreur lors du chargement du nouveau client:", error);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la sauvegarde automatique:", error);
      }
    };

    // Délai de 2 secondes pour éviter trop de sauvegardes
    const timeoutId = setTimeout(sauvegarderAutomatiquement, 2000);
    return () => clearTimeout(timeoutId);
  }, [devisModifie, isEditMode, devisExistant, modeConserve, isOptimizing]);

  // Détecter les modifications du devis
  useEffect(() => {
    if (!devisInitial || !isEditMode) return;

    // Comparer l'état actuel avec l'état initial
    const articlesModifies = JSON.stringify(articlesDevis) !== JSON.stringify(devisInitial.articles);
    const optimisationModifiee = optimisationActive !== devisInitial.optimisationActive;
    const devisOptimiseModifie = JSON.stringify(devisOptimise) !== JSON.stringify(devisInitial.devisOptimise);
    const finalisationModifiee = JSON.stringify(finalisationData) !== JSON.stringify(devisInitial.finalisationData || {});
    const remisesModifiees = JSON.stringify(remisesAppliquees) !== JSON.stringify(devisInitial.remises || { mainOeuvre: 0, matieresFournitures: 0, typeRemise: 'detaillee' });

    const hasChanges = articlesModifies || optimisationModifiee || devisOptimiseModifie || finalisationModifiee || remisesModifiees;
    setDevisModifie(hasChanges);
  }, [articlesDevis, optimisationActive, devisOptimise, finalisationData, remisesAppliquees, devisInitial, isEditMode]);

  // Hook pour vérifier les modifications de tarifs
  const { hasModifications, modifications } = useTarifsAlert({
    dateReference: isEditMode && devisExistant && !tarifsAlertProcessed ? (devisExistant.dateDerniereModification || devisExistant.date) : null,
    onAlert: () => {
      if (!tarifsAlertProcessed) {

        setShowTarifsAlert(true);
      } else {

      }
    },
    enabled: isEditMode && !!devisExistant && !tarifsAlertProcessed
  });

  // Générer le nom technique de l'article
  const genererNomTechnique = (): string => {
    const { forme, dimensions, options } = article;
    const formesLibelles: Record<string, string> = {
      rectangulaire_bandeau: 'Rectangulaire avec bandeau',
      rectangulaire_joues: 'Rectangulaire avec joues',
      rond_bandeau: 'Rond avec bandeau',
      cylindre_joues: 'Cylindre avec joues',
      rectangle_simple: 'Rectangle simple',
    };

    let nomTechnique = formesLibelles[forme] || forme;
    
    // Carré si longueur = largeur
    if (forme.includes('rectangulaire') && dimensions.longueur && dimensions.largeur && dimensions.longueur === dimensions.largeur) {
      nomTechnique = nomTechnique.replace('Rectangulaire', 'Carré');
    }

    // Ajouter dimensions selon la forme
    if (dimensions) {
      if (forme === 'rectangulaire_bandeau' || forme === 'rectangulaire_joues') {
        if (dimensions.longueur && dimensions.largeur && dimensions.hauteur) {
          nomTechnique += ` ${dimensions.longueur}x${dimensions.largeur}x${dimensions.hauteur}`;
        }
      } else if (forme === 'rond_bandeau') {
        if (dimensions.diametre && dimensions.hauteur) {
          nomTechnique += ` Ø${dimensions.diametre} h=${dimensions.hauteur}`;
        }
      } else if (forme === 'cylindre_joues') {
        if (dimensions.diametre && dimensions.longueur) {
          nomTechnique += ` Ø${dimensions.diametre} L=${dimensions.longueur}`;
        }
      } else if (forme === 'rectangle_simple') {
        if (dimensions.longueur && dimensions.largeur) {
          nomTechnique += ` ${dimensions.longueur}x${dimensions.largeur}`;
        }
      }
    }

    // Ajouter options
    if (options) {
      const optionsTexte = [];
      if (options.fond) optionsTexte.push('avec fond');
      if (options.grille) optionsTexte.push('avec grille');
      if (options.mousse) optionsTexte.push(`mousse ${options.mousse}`);
      if (options.fermeture) optionsTexte.push(`fermeture ${options.fermeture}`);
      if (options.surpiqure) optionsTexte.push('surpiqûre');
      if (options.formeComplexe) optionsTexte.push('forme complexe');
      
      if (forme === 'rectangle_simple') {
        if (options.oeillets) optionsTexte.push('avec œillets');
        if (options.renforts) optionsTexte.push('avec renforts aux angles');
        if (options.sangles) optionsTexte.push('avec sangles');
        if (options.assemblageAuto) optionsTexte.push('assemblage automatique');
      }

      if (optionsTexte.length > 0) {
        nomTechnique += ` (${optionsTexte.join(', ')})`;
      }
    }

    return nomTechnique;
  };

  const handleDataChange = () => setDonneesModifiees(true);

  // Fonction pour créer un devis brouillon automatiquement
  const creerDevisBrouillon = async () => {
    try {
      const nouveauDevis = {
        reference: '', // Sera générée automatiquement par le backend
        client: clientId || null,
        date: new Date().toISOString(),
        statut: 'NOUVEAU' as const, // Commencer par NOUVEAU, passera à BROUILLON dès le premier article
        articles: [],
        groupesMatiere: [],
        coutMatiereTotal: 0,
        coutMainOeuvreTotal: 0,
        coutAccessoiresTotal: 0,
        coutPetitesFournitures: 0,
        montantTotal: 0,
        optimisationActive: false,
        devisOptimise: null,
        finalisationData: {}
      };

      const devisSauvegarde = await devisApiService.saveDevis(nouveauDevis);
      
      return devisSauvegarde;
    } catch (error) {
      console.error("Erreur lors de la création du devis brouillon:", error);
      throw error;
    }
  };

  // Fonction pour calculer le statut automatique du devis
  const calculerStatutDevis = () => {
    
    // Vérifications par ordre de priorité selon la spécification backend
    if (devisExistant?.marque_comme_livre) {
      return 'TERMINE_LIVRE';
    }
    
    if (devisExistant?.marque_comme_accepte) {
      return 'ACCEPTE';
    }
    
    if (devisExistant?.pdf_genere_le) {
      return 'EN_ATTENTE_RETOUR_CLIENT';
    }
    
    if (articlesDevis.length === 0) {
      return 'NOUVEAU';
    }
    
    // Utiliser le clientId des données de finalisation ou l'URL
    const clientIdEffectif = finalisationData.clientId || clientId;
    // Utiliser le projet des données de finalisation ou du devis existant
    const projetEffectif = finalisationData.nomProjet || devisExistant?.projet;
    
    if (!clientIdEffectif || !projetEffectif) {
      return 'BROUILLON';
    }
    
    // Vérifier si les données de finalisation sont complètes
    const conditionsCompletes = finalisationData.conditionsPaiement && 
                                finalisationData.delaisLivraison &&
                                finalisationData.signatureConfig?.nomSignataire;
    
    if (conditionsCompletes) {
      return 'COMPLET';
    }
    
    return 'ENREGISTRE';
  };

  // Calculer le total avec économie et remises si applicables
  const calculerTotal = () => {
    const totalArticles = articlesDevis.reduce((total, article) => total + article.prixUnitaire * article.quantite, 0);
    const economie = (hasPremiumFeatures && devisOptimise && optimisationActive && devisOptimise.economieTotal !== undefined && Math.abs(Number(devisOptimise.economieTotal)) > 0.01) 
      ? devisOptimise.economieTotal 
      : 0;
    
    // ✅ CORRECTION: Ne déduire l'économie QUE si integrerEconomieCalepinage === true
    const economieADeduire = integrerEconomieCalepinage ? economie : 0;
    
    // Calculer les remises appliquées
    const totalRemises = calculerMontantRemises();
    
    return totalArticles - economieADeduire - totalRemises;
  };

  // Calculer le montant des remises appliquées
  const calculerMontantRemises = () => {
    if (remisesAppliquees.typeRemise === 'globale' && (remisesAppliquees.globale || 0) > 0) {
      const totalSansEconomie = articlesDevis.reduce((total, article) => total + article.prixUnitaire * article.quantite, 0);
      const economie = (hasPremiumFeatures && devisOptimise && optimisationActive && devisOptimise.economieTotal !== undefined && Math.abs(Number(devisOptimise.economieTotal)) > 0.01) 
        ? Math.abs(devisOptimise.economieTotal) 
        : 0;
      return (totalSansEconomie - economie) * (remisesAppliquees.globale || 0) / 100;
    } else if (remisesAppliquees.typeRemise === 'detaillee') {
      let totalRemises = 0;
      
      // Calculer les coûts par catégorie
      let coutMainOeuvre = 0;
      let coutMatieresFournitures = 0;

      articlesDevis.forEach(article => {
        const quantite = article.quantite || 1;
        if ((article as any).estArticleLibre) {
          const typeArticleLibre = (article as any).typeArticleLibre;
          const montant = article.prixUnitaire * quantite;
          if (typeArticleLibre === 'matiere' || typeArticleLibre === 'sous_traitance') {
            coutMatieresFournitures += montant;
          } else if (typeArticleLibre === 'main_oeuvre') {
            coutMainOeuvre += montant;
          }
        } else if (article.prixCalcule?.details?.couts) {
          const couts = article.prixCalcule.details.couts;
          coutMainOeuvre += (couts.main_oeuvre || 0);
          coutMatieresFournitures += (couts.matieres || 0) + (couts.accessoires || 0);
        } else {
          const montantTotal = article.prixUnitaire * quantite;
          coutMainOeuvre += montantTotal;
        }
      });

      // Appliquer les remises
      if (remisesAppliquees.mainOeuvre > 0) {
        totalRemises += coutMainOeuvre * remisesAppliquees.mainOeuvre / 100;
      }
      if (remisesAppliquees.matieresFournitures > 0) {
        totalRemises += coutMatieresFournitures * remisesAppliquees.matieresFournitures / 100;
      }

      return totalRemises;
    }
    
    return 0;
  };

  // ✅ Calculer les montants des remises pour le PDF (sans effet de bord)
  const calculerMontantsRemisesPourPDF = () => {
    if (remisesAppliquees.typeRemise === 'globale' && (remisesAppliquees.globale || 0) > 0) {
      const totalSansEconomie = articlesDevis.reduce((total, article) => total + article.prixUnitaire * article.quantite, 0);
      const economie = (hasPremiumFeatures && devisOptimise && optimisationActive && devisOptimise.economieTotal !== undefined && Math.abs(Number(devisOptimise.economieTotal)) > 0.01) 
        ? Math.abs(devisOptimise.economieTotal) 
        : 0;
      const montantCalcule = (totalSansEconomie - economie) * (remisesAppliquees.globale || 0) / 100;
      
      return {
        ...remisesAppliquees,
        montantCalcule
      };
    } else if (remisesAppliquees.typeRemise === 'detaillee') {
      // Calculer les coûts par catégorie
      let coutMainOeuvre = 0;
      let coutMatieresFournitures = 0;

      articlesDevis.forEach(article => {
        const quantite = article.quantite || 1;
        if ((article as any).estArticleLibre) {
          const typeArticleLibre = (article as any).typeArticleLibre;
          const montant = article.prixUnitaire * quantite;
          if (typeArticleLibre === 'matiere' || typeArticleLibre === 'sous_traitance') {
            coutMatieresFournitures += montant;
          } else if (typeArticleLibre === 'main_oeuvre') {
            coutMainOeuvre += montant;
          }
        } else if (article.prixCalcule?.details?.couts) {
          const couts = article.prixCalcule.details.couts;
          coutMainOeuvre += (couts.main_oeuvre || 0);
          coutMatieresFournitures += (couts.matieres || 0) + (couts.accessoires || 0);
        } else {
          const montantTotal = article.prixUnitaire * quantite;
          coutMainOeuvre += montantTotal;
        }
      });

      const montantMO = remisesAppliquees.mainOeuvre > 0 ? coutMainOeuvre * remisesAppliquees.mainOeuvre / 100 : 0;
      const montantMatFour = remisesAppliquees.matieresFournitures > 0 ? coutMatieresFournitures * remisesAppliquees.matieresFournitures / 100 : 0;

      return {
        ...remisesAppliquees,
        montantMO,
        montantMatFour
      };
    }
    
    return remisesAppliquees;
  };

  // Gérer l'application des remises
  const handleAppliquerRemises = (remises: typeof remisesAppliquees) => {
    setRemisesAppliquees(remises);
    setDevisModifie(true);
    setConfirmationMessage('Remises appliquées avec succès !');
    setTimeout(() => setConfirmationMessage(null), 3000);
  };

  // Callback pour conserver le devis (mode lecture seule temporaire)
  const handleConserverDevis = () => {
    setModeConserve(true);
    setConfirmationMessage('✅ Devis conservé en mode lecture seule. Seule la génération PDF est autorisée.');
    setTimeout(() => setConfirmationMessage(null), 5000);
  };

  // Gérer les changements dans le formulaire
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    section?: string,
    subsection?: string
  ) => {
    const { name, value, type } = e.target;
    setDonneesModifiees(true);
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      
      if (section === 'options') {
        setArticle(prev => ({
          ...prev,
          options: { ...prev.options, [name]: checked },
        }));
      } else {
        setArticle(prev => ({ ...prev, [name]: checked }));
      }
    } else if (section === 'dimensions') {
      setArticle(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [name]: value === '' ? undefined : Number(value),
        },
      }));
    } else if (section === 'options') {
      const nextValue =
        name === 'talonAgrafage'
          ? (value === '' ? undefined : Number(value))
          : value;
      setArticle(prev => ({
        ...prev,
        options: { ...prev.options, [name]: nextValue },
      }));
    } else if (section === 'materiaux' && subsection) {
      setArticle(prev => ({
        ...prev,
        materiaux: {
          ...prev.materiaux,
          [subsection]: {
            ...prev.materiaux[subsection as keyof typeof prev.materiaux],
            [name]: value,
          },
        },
      }));
    } else if (name === 'type') {
      // Réinitialiser selon le type
      if (value === 'bache') {
        setArticle(prev => ({
          ...prev,
          type: 'bache',
          forme: 'rectangle_simple',
          dimensions: {
            longueur: prev.dimensions.longueur || 100,
            largeur: prev.dimensions.largeur || 80,
            talon: 5,
          },
            options: {
              fond: false,
              grille: false,
              talonAgrafageActif: false,
              talonAgrafage: 3,
              mousse: '',
              fermeture: '',
              surpiqure: false,
              formeComplexe: false,
              oeillets: false,
            espacementOeillets: 30,
            renforts: false,
            nombreRenforts: 4,
            sangles: false,
            nombreSangles: 2,
            assemblageAuto: true,
          },
        }));
      } else {
        setArticle(prev => ({
          ...prev,
          type: 'coussin',
          forme: '', // Pas de forme par défaut
          dimensions: {
            longueur: prev.dimensions.longueur || 50,
            largeur: prev.dimensions.largeur || 30,
            hauteur: prev.dimensions.hauteur || 10,
            talon: 1,
          },
            options: {
              fond: true,
              grille: false,
              talonAgrafageActif: false,
              talonAgrafage: 3,
              mousse: 'mousse_003', // Utiliser l'ID de la mousse 30A/8 MOUSSE STANDARD 30 A
              fermeture: 'glissiere',
              surpiqure: false,
              formeComplexe: false,
            },
        }));
      }
    } else if (name === 'forme') {
      // Adapter dimensions selon la forme
      const talon = article.dimensions.talon || (article.type === 'bache' ? 5 : 1);
      
      let newDimensions: any;
      if (value === 'rectangulaire_bandeau' || value === 'rectangulaire_joues') {
        newDimensions = {
          longueur: article.dimensions.longueur || 50,
          largeur: article.dimensions.largeur || 30,
          hauteur: article.dimensions.hauteur || 10,
          talon
        };
      } else if (value === 'rectangle_boite') {
        // ✅ CORRECTION: Assurer une hauteur par défaut de 10 pour Rectangle Boîte
        newDimensions = {
          longueur: article.dimensions.longueur || 50,
          largeur: article.dimensions.largeur || 30,
          hauteur: article.dimensions.hauteur || 10, // Toujours 10 par défaut
          talon
        };
      } else if (value === 'rond_bandeau' || value === 'rond_avec_bandeau') {
        newDimensions = {
          diametre: article.dimensions.diametre || article.dimensions.longueur || 50,
          hauteur: article.dimensions.hauteur || article.dimensions.largeur || 10,
          talon
        };
      } else if (value === 'cylindre_joues') {
        newDimensions = {
          diametre: article.dimensions.diametre || article.dimensions.largeur || 10,
          longueur: article.dimensions.longueur || 50,
          talon
        };
      } else if (value === 'rectangulaire_demi_bandeau') {
        newDimensions = {
          longueur: article.dimensions.longueur || 50,
          largeur: article.dimensions.largeur || 30,
          hauteur: article.dimensions.hauteur || 10,
          talon
        };
      } else {
        newDimensions = {
          longueur: article.dimensions.longueur || 100,
          largeur: article.dimensions.largeur || 80,
          talon
        };
      }
      
      setArticle(prev => ({
        ...prev,
        forme: value,
        dimensions: newDimensions
      }));
    } else {
      setArticle(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value,
      }));
    }
  };

  // Calculer le prix
  const calculerPrix = async () => {
    setError(null);
    
    // Validations
    if (!article.nom) {
      setError('Veuillez saisir un nom pour l\'article');
      // Faire défiler vers le champ nom et le mettre en évidence
      const nomInput = document.getElementById('nom');
      if (nomInput) {
        nomInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        nomInput.focus();
        // Ajouter une classe d'erreur temporaire
        nomInput.classList.add('border-red-500', 'ring-2', 'ring-red-200');
        setTimeout(() => {
          nomInput.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
        }, 5000);
      }
      return;
    }

    if (!article.forme) {
      setError('Veuillez sélectionner une forme pour l\'article');
      // Focus sur la section de sélection de forme (dans TypeForme)
      const formeSection = document.querySelector('[data-section="forme"]') || 
                          document.querySelector('.forme-selection') ||
                          // Chercher le bouton "Sélectionner" dans la section forme
                          Array.from(document.querySelectorAll('button')).find(btn => 
                            btn.textContent?.includes('Sélectionner') || btn.textContent?.includes('Changer')
                          );
      
      if (formeSection) {
        formeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Ajouter une surbrillance à la section forme
        formeSection.classList.add('ring-4', 'ring-red-300', 'ring-opacity-75');
        setTimeout(() => {
          if (formeSection) {
            formeSection.classList.remove('ring-4', 'ring-red-300', 'ring-opacity-75');
          }
        }, 5000);
        
        // Si c'est un bouton, le cliquer pour ouvrir la modale
        if (formeSection.tagName === 'BUTTON') {
          (formeSection as HTMLButtonElement).click();
        }
      }
      return;
    }

    if (!article.materiaux.dessus.matiere || !article.materiaux.dessus.couleur ||
        !article.materiaux.dessous.matiere || !article.materiaux.dessous.couleur ||
        !article.materiaux.cotes.matiere || !article.materiaux.cotes.couleur) {
      setError('Veuillez sélectionner tous les matériaux et couleurs');
      // Focus sur la première matière manquante
      let firstMissingField: HTMLElement | null = null;
      if (!article.materiaux.dessus.matiere) {
        firstMissingField = document.getElementById('dessus-matiere');
      } else if (!article.materiaux.dessus.couleur) {
        firstMissingField = document.getElementById('dessus-couleur');
      } else if (!article.materiaux.dessous.matiere) {
        firstMissingField = document.getElementById('dessous-matiere');
      } else if (!article.materiaux.dessous.couleur) {
        firstMissingField = document.getElementById('dessous-couleur');
      } else if (!article.materiaux.cotes.matiere) {
        firstMissingField = document.getElementById('cotes-matiere');
      } else if (!article.materiaux.cotes.couleur) {
        firstMissingField = document.getElementById('cotes-couleur');
      }
      
      if (firstMissingField) {
        firstMissingField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstMissingField.focus();
        firstMissingField.classList.add('border-red-500', 'ring-2', 'ring-red-200');
        setTimeout(() => {
          if (firstMissingField) {
            firstMissingField.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
          }
        }, 5000);
      }
      return;
    }

    // Validation spécifique pour l'option grille
    if (article.options.grille && article.forme !== 'cylindre_joues') {
      if (!article.options.grilleMatiere || !article.options.grilleCouleur) {
        setError('Veuillez sélectionner la matière et la couleur pour la grille de drainage');
        // Pour les champs grille, on fait défiler vers la section grille
        const grilleSection = document.querySelector('[data-section="grille"]') || 
                             document.querySelector('.grille-section') ||
                             document.getElementById('grille');
        
        if (grilleSection) {
          grilleSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Ajouter une surbrillance à toute la section grille
          grilleSection.classList.add('ring-4', 'ring-red-300', 'ring-opacity-75');
          setTimeout(() => {
            grilleSection.classList.remove('ring-4', 'ring-red-300', 'ring-opacity-75');
          }, 5000);
        }
        return;
      }
    }

    // Validation des dimensions selon la forme (basée sur SPECIFICATION_COMPLETE_FORMES.md)
    if (article.forme === 'rectangulaire_bandeau' || article.forme === 'rectangulaire_joues' || 
        article.forme === 'rectangulaire_demi_bandeau' || article.forme === 'rectangle_boite') {
      // Paramètres : L (longueur), l (largeur), H (hauteur)
      if (!article.dimensions.longueur || article.dimensions.longueur <= 0 ||
          !article.dimensions.largeur || article.dimensions.largeur <= 0 ||
          !article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
        setError('Veuillez saisir toutes les dimensions (longueur, largeur, hauteur)');
        // Focus sur le premier champ de dimension manquant
        let firstMissingDimension: HTMLElement | null = null;
        if (!article.dimensions.longueur || article.dimensions.longueur <= 0) {
          firstMissingDimension = document.getElementById('longueur');
        } else if (!article.dimensions.largeur || article.dimensions.largeur <= 0) {
          firstMissingDimension = document.getElementById('largeur');
        } else if (!article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
          firstMissingDimension = document.getElementById('hauteur');
        }
        
        if (firstMissingDimension) {
          firstMissingDimension.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissingDimension.focus();
          firstMissingDimension.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            if (firstMissingDimension) {
              firstMissingDimension.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
            }
          }, 5000);
        }
        return;
      }
    } else if (article.forme === 'rond_bandeau' || article.forme === 'rond_avec_bandeau') {
      // Paramètres : D (diamètre), H (hauteur)
      if (!article.dimensions.diametre || article.dimensions.diametre <= 0 ||
          !article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
        setError('Veuillez saisir toutes les dimensions (diamètre, hauteur)');
        // Focus sur le premier champ de dimension manquant
        let firstMissingDimension: HTMLElement | null = null;
        if (!article.dimensions.diametre || article.dimensions.diametre <= 0) {
          firstMissingDimension = document.getElementById('diametre');
        } else if (!article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
          firstMissingDimension = document.getElementById('hauteur');
        }
        
        if (firstMissingDimension) {
          firstMissingDimension.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissingDimension.focus();
          firstMissingDimension.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            if (firstMissingDimension) {
              firstMissingDimension.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
            }
          }, 5000);
        }
        return;
      }
    } else if (article.forme === 'cylindre_joues') {
      // Paramètres : D (diamètre), L (longueur)
      if (!article.dimensions.diametre || article.dimensions.diametre <= 0 ||
          !article.dimensions.longueur || article.dimensions.longueur <= 0) {
        setError('Veuillez saisir toutes les dimensions (diamètre, longueur)');
        // Focus sur le premier champ de dimension manquant
        let firstMissingDimension: HTMLElement | null = null;
        if (!article.dimensions.diametre || article.dimensions.diametre <= 0) {
          firstMissingDimension = document.getElementById('diametre');
        } else if (!article.dimensions.longueur || article.dimensions.longueur <= 0) {
          firstMissingDimension = document.getElementById('longueur');
        }
        
        if (firstMissingDimension) {
          firstMissingDimension.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissingDimension.focus();
          firstMissingDimension.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            if (firstMissingDimension) {
              firstMissingDimension.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
            }
          }, 5000);
        }
        return;
      }
    } else if (article.forme === 'rectangle_simple') {
      // Paramètres : L (longueur), l (largeur)
      if (!article.dimensions.longueur || article.dimensions.longueur <= 0 ||
          !article.dimensions.largeur || article.dimensions.largeur <= 0) {
        setError('Veuillez saisir toutes les dimensions (longueur, largeur)');
        // Focus sur le premier champ de dimension manquant
        let firstMissingDimension: HTMLElement | null = null;
        if (!article.dimensions.longueur || article.dimensions.longueur <= 0) {
          firstMissingDimension = document.getElementById('longueur');
        } else if (!article.dimensions.largeur || article.dimensions.largeur <= 0) {
          firstMissingDimension = document.getElementById('largeur');
        }
        
        if (firstMissingDimension) {
          firstMissingDimension.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissingDimension.focus();
          firstMissingDimension.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            if (firstMissingDimension) {
              firstMissingDimension.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
            }
          }, 5000);
        }
        return;
      }
    } else if (article.forme === 'rectangle_joues_biseau' || article.forme === 'rectangle_demi_bandeau_biseau') {
      // Paramètres : L (longueur), l1 (largeur dessus), l2 (largeur dessous), H (hauteur)
      if (!article.dimensions.longueur || article.dimensions.longueur <= 0 ||
          !article.dimensions.largeur_dessus || article.dimensions.largeur_dessus <= 0 ||
          !article.dimensions.largeur_dessous || article.dimensions.largeur_dessous <= 0 ||
          !article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
        setError('Veuillez saisir toutes les dimensions (longueur, largeur dessus, largeur dessous, hauteur)');
        // Focus sur le premier champ de dimension manquant
        let firstMissingDimension: HTMLElement | null = null;
        if (!article.dimensions.longueur || article.dimensions.longueur <= 0) {
          firstMissingDimension = document.getElementById('longueur');
        } else if (!article.dimensions.largeur_dessus || article.dimensions.largeur_dessus <= 0) {
          firstMissingDimension = document.getElementById('largeur_dessus');
        } else if (!article.dimensions.largeur_dessous || article.dimensions.largeur_dessous <= 0) {
          firstMissingDimension = document.getElementById('largeur_dessous');
        } else if (!article.dimensions.hauteur || article.dimensions.hauteur <= 0) {
          firstMissingDimension = document.getElementById('hauteur');
        }
        
        if (firstMissingDimension) {
          firstMissingDimension.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstMissingDimension.focus();
          firstMissingDimension.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            if (firstMissingDimension) {
              firstMissingDimension.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
            }
          }, 5000);
        }
        return;
      }
      // Validation spécifique biseau : l2 > l1 (largeur dessous > largeur dessus)
      if (article.dimensions.largeur_dessous <= article.dimensions.largeur_dessus) {
        setError('Pour un biseau, la largeur dessous doit être supérieure à la largeur dessus');
        // Focus sur le champ largeur_dessous pour correction
        const largeurDessousField = document.getElementById('largeur_dessous');
        if (largeurDessousField) {
          largeurDessousField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          largeurDessousField.focus();
          largeurDessousField.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          setTimeout(() => {
            largeurDessousField.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
          }, 5000);
        }
        return;
      }
    }

    if (!article.dimensions.talon || article.dimensions.talon <= 0) {
      setError('Veuillez saisir une valeur valide pour le talon');
      // Focus sur le champ talon
      const talonField = document.getElementById('talon');
      if (talonField) {
        talonField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        talonField.focus();
        talonField.classList.add('border-red-500', 'ring-2', 'ring-red-200');
        setTimeout(() => {
          talonField.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
        }, 5000);
      }
      return;
    }

    setCalculating(true);

    try {
      const articleToSend = {
        ...article,
        dimensions: {
          ...article.dimensions,
          talon: Number(article.dimensions.talon)
        }
      };

      // ✅ CORRECTION : Supprimer prixCalcule avant envoi au backend
      delete articleToSend.prixCalcule;
      
      const resultatCalcul = await apiService.calculerPrix(articleToSend);
      
      setPrixCalcule(resultatCalcul);

      const prixTotalArticle = resultatCalcul.prixTotalArticle || resultatCalcul.prix;
      const prixUnitaire = resultatCalcul.prixUnitaire ?? (prixTotalArticle / article.quantite);
      
      setArticle(prev => ({
        ...prev,
        prixUnitaire: prixUnitaire,
        prixTotalArticle: prixTotalArticle,
        nomTechnique: genererNomTechnique(),
        prixCalcule: resultatCalcul,  // ✅ Utiliser la nouvelle réponse du backend
      }));

      setDonneesModifiees(false);
      setShowDetailsPrix(true);
    } catch (error: any) {
      console.error("🔍 FRONTEND - Erreur lors du calcul du prix:", error);
      setError("Une erreur est survenue lors du calcul du prix");
    } finally {
      setCalculating(false);
    }
  };

  // Réinitialiser le formulaire
  const reinitialiserFormulaire = () => {
    setArticle({
      nom: '',
      type: 'coussin',
      forme: '', // Pas de forme par défaut
      dimensions: { longueur: 50, largeur: 30, hauteur: 10, talon: 1 },
      options: {
        fond: true,
        grille: false,
        talonAgrafageActif: false,
        talonAgrafage: 3,
        mousse: 'mousse_003', // Utiliser l'ID de la mousse 30A/8 MOUSSE STANDARD 30 A
        fermeture: 'glissiere',
        surpiqure: false,
        formeComplexe: false,
      },
      materiaux: {
        dessus: { matiere: '', couleur: '' },
        dessous: { matiere: '', couleur: '' },
        cotes: { matiere: '', couleur: '' },
      },
      quantite: 1,
      prixUnitaire: 0,
    });
    setPrixCalcule(null);
    setShowDetailsPrix(false);
    setDonneesModifiees(false);
  };

  const fermerArticleModal = useCallback(() => {
    if (donneesModifiees) {
      const confirmer = window.confirm('Vous avez des modifications non enregistrées sur l\'article. Fermer la modale ?');
      if (!confirmer) return;
    }
    setShowArticleModal(false);
  }, [donneesModifiees]);

  useEffect(() => {
    if (!showArticleModal) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        fermerArticleModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showArticleModal, fermerArticleModal]);

  // Vérifier unicité du nom
  const verifierNomUnique = (nomArticle: string, articleId?: string) => {
    if (!nomArticle) return { unique: true };
    
    const articleExistant = articlesDevis.find(a => 
      a.nom === nomArticle && (!articleId || a.id !== articleId)
    );
    
    if (articleExistant) {
      let compteur = 1;
      let nouveauNom = `${nomArticle} ${compteur}`;
      
      while (articlesDevis.some(a => a.nom === nouveauNom && (!articleId || a.id !== articleId))) {
        compteur++;
        nouveauNom = `${nomArticle} ${compteur}`;
      }
      
      return { unique: false, nouveauNom };
    }
    
    return { unique: true };
  };

  // Modifier un article
  const modifierArticle = (articleToEdit: Article) => {
    if ((articleToEdit as any).estArticleLibre) {
      setArticleLibreEnEdition(articleToEdit);
      setShowArticleLibreModal(true);
      return;
    }

    setArticle(articleToEdit);
    setIsEditingArticle(true);
    setShowArticleModal(true);
    setPrixCalcule(null);
    setShowDetailsPrix(false);
    setDonneesModifiees(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // 🔴 HELPER : Invalider l'optimisation immédiatement (éliminer fenêtre "clic rapide")
  const invaliderOptimisationImmediatement = async () => {
    // ✅ CORRECTION: Ne conditionner QUE sur l'existence du devisId
    if (!devisExistant?.id) return;
    
    try {
      console.log('🔴 [INVALIDATE] Appel immédiat pour invalider optimisation');
      // ✅ CORRECTION: Utiliser le wrapper devisApiService (gestion auth + base URL)
      await devisApiService.invaliderOptimisation(devisExistant.id);
    } finally {
      // ✅ CORRECTION: Approche optimiste - mettre à jour l'UI quoi qu'il arrive
      setOptimisationActive(false);
      setDevisOptimise(null);
      setJustInvalidated(true); // ✅ NOUVEAU: Activer le flag pour afficher l'alerte
      console.log('✅ [INVALIDATE] État UI mis à jour (optimiste)');
    }
  };

  // Supprimer un article
  const supprimerArticle = async (articleId: string) => {
    setArticlesDevis(prev => prev.filter(a => a.id !== articleId));
    setConfirmationMessage("Article supprimé avec succès !");
    setTimeout(() => setConfirmationMessage(null), 3000);
    
    // ✅ CORRECTION: Invalider immédiatement l'optimisation (éliminer fenêtre "clic rapide")
    await invaliderOptimisationImmediatement();
    
    // Marquer le devis comme modifié
    setDevisModifie(true);
  };
  
  // Ajouter un nouvel article
  const ajouterNouvelArticle = () => {
    reinitialiserFormulaire();
    setIsEditingArticle(false);
    setShowArticleModal(true);
    // Faire défiler vers le haut pour voir le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Mettre à jour un article
  const mettreAJourArticle = async () => {
    if (!prixCalcule) {
      setError('Veuillez calculer le prix avant de mettre à jour l\'article');
      return;
    }
    
    const { unique, nouveauNom } = verifierNomUnique(article.nom, article.id);
    
    if (!unique && nouveauNom) {
      const confirmer = window.confirm(
        `Le nom "${article.nom}" existe déjà. Utiliser "${nouveauNom}" ?`
      );
      
      if (confirmer) {
        setArticle(prev => ({ ...prev, nom: nouveauNom }));
        return;
      } else {
        document.getElementById('nom')?.focus();
        return;
      }
    }

    try {
      const prixUnitaireActuel = prixCalcule.prixUnitaire ?? ((prixCalcule.prixTotalArticle || prixCalcule.prix) / article.quantite);

      // Créer l'article avec le prix recalculé
      const articleAvecPrixActuel = {
        ...article,
        prixUnitaire: prixUnitaireActuel,
        prixCalcule: prixCalcule,
        nomTechnique: genererNomTechnique()
      };

      const articleSauvegarde = await apiService.saveArticle(articleAvecPrixActuel);

      // ✅ Utiliser le prix recalculé, pas celui de l'état article
      const articleMisAJour = {
        ...articleSauvegarde,
        prixUnitaire: prixUnitaireActuel,
        prixCalcule: prixCalcule
      };

      setArticlesDevis(prev => {
        const updated = prev.map(a => {
          if (a.id === article.id) {

            return articleMisAJour;
          }
          return a;
        });

        return updated;
      });

      // ✅ CORRECTION: Invalider immédiatement l'optimisation (éliminer fenêtre "clic rapide")
      await invaliderOptimisationImmediatement();

      // ✅ CORRECTION: Marquer le devis comme modifié
      setDevisModifie(true);

      setConfirmationMessage(`Article "${articleMisAJour.nom}" mis à jour avec succès ! Prix: ${articleMisAJour.prixUnitaire.toFixed(2)}€`);
      setTimeout(() => setConfirmationMessage(null), 3000);

      // Masquer le formulaire après mise à jour
      setShowArticleModal(false);
      setIsEditingArticle(false);

      setTimeout(() => {
        document.getElementById('tableau-articles')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error("❌ [Mise à jour] Erreur:", error);
      setError("Erreur lors de la mise à jour");
    }
  };

  // Ajouter au devis
  const ajouterAuDevis = async () => {
    if (!prixCalcule) {
      setError('Veuillez calculer le prix avant d\'ajouter l\'article au devis');
      return;
    }


    const { unique, nouveauNom } = verifierNomUnique(article.nom);
    const nomFinal = !unique && nouveauNom ? nouveauNom : article.nom;

    try {
      const prixUnitaire = prixCalcule.prixUnitaire ?? ((prixCalcule.prixTotalArticle || prixCalcule.prix) / article.quantite);

      const articleAvecPrix = {
        ...article,
        nom: nomFinal,
        id: article.id || `article-${Date.now()}`,
        prixUnitaire,
        prixCalcule,
        nomTechnique: genererNomTechnique(),
      };
      
      const articleSauvegarde = await apiService.saveArticle(articleAvecPrix);
      
      const articleFinal: Article = {
        ...articleSauvegarde,
        prixUnitaire: articleAvecPrix.prixUnitaire,
        prixCalcule: articleAvecPrix.prixCalcule,
      };
      
      // Si c'est le premier article et qu'on n'est pas en mode édition, créer le devis automatiquement
      if (!isEditMode && articlesDevis.length === 0) {
        try {
          const nouveauDevis = await creerDevisBrouillon();
          setDevisExistant(nouveauDevis);
          
          // Passer en mode édition
          navigate(`/devis/configurateur/${nouveauDevis.id}`, { replace: true });
          
          setConfirmationMessage(`Devis "${nouveauDevis.reference}" créé automatiquement !`);
          setTimeout(() => setConfirmationMessage(null), 3000);
        } catch (error) {
          console.error("Erreur lors de la création automatique du devis:", error);
          setError("Erreur lors de la création du devis");
          return;
        }
      }

      setArticlesDevis(prev => [...prev, articleFinal]);
      setConfirmationMessage(`Article "${articleFinal.nom}" ajouté !`);
      setTimeout(() => setConfirmationMessage(null), 3000);

      // ✅ CORRECTION: Invalider immédiatement l'optimisation (éliminer fenêtre "clic rapide")
      await invaliderOptimisationImmediatement();

      // Marquer le devis comme modifié
      setDevisModifie(true);

      // Si l'optimisation est active, la recalculer automatiquement
      if (optimisationActive && hasPremiumFeatures) {
        setTimeout(() => {
          optimiserDevis(true);
        }, 100);
      }

      // Masquer le formulaire après ajout
      setShowArticleModal(false);
      reinitialiserFormulaire();

      setTimeout(() => {
        document.getElementById('tableau-articles')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error("Erreur ajout:", error);
      setError("Erreur lors de l'ajout");
    }
  };

  // Afficher les détails du prix d'un article
  const afficherDetailsPrix = (article: Article) => {
    setArticleDetailsPrix(article);
    setShowDetailsPrixModal(true);
  };

  const sauvegarderNotesInternes = async () => {
    if (!isEditMode || !devisExistant?.id) return;
    try {
      setNotesSaving(true);
      setNotesMessage(null);
      const updated = await apiFetch<any>(`/devis/${devisExistant.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          id: devisExistant.id,
          notes_internes: notesInternes,
        }),
      });
      setDevisExistant(updated as any);
      setNotesMessage('Notes internes enregistrées');
    } catch (err: any) {
      setError(err?.message || "Erreur lors de l'enregistrement des notes internes");
    } finally {
      setNotesSaving(false);
    }
  };

  const [isCalepinageRefreshing, setIsCalepinageRefreshing] = useState(false);

  const fetchVignettesBackend = async (devisIdParam?: string) => {
    const devisIdCible = devisIdParam || devisExistant?.id || id;
    if (!devisIdCible) return;

    try {
      const ts = Date.now();
      await apiFetch<any>(`/devis/${devisIdCible}/calepinage/vignettes?ts=${ts}`);
    } catch (error) {
      console.error("Erreur lors du chargement des vignettes backend:", error);
    }
  };


  const optimiserDevis = async (forceOptimisation = false) => {
    // Blocage UI si mode non autorisé
    if (!access || (access.access_mode !== 'active' && access.access_mode !== 'trial')) {
      window.dispatchEvent(
        new CustomEvent('access_denied', {
          detail: { access_mode: access?.access_mode || 'preview', required: 'active' },
        })
      );
      return;
    }
    
    if (hasPremiumFeatures && articlesDevis.length > 0 && matieres.length > 0 && (optimisationActive || forceOptimisation)) {
      setIsOptimizing(true);
      setIsCalepinageRefreshing(true);
      
      try {
        console.log('🚀 [FRONTEND] Optimisation lancée - Baseline + Optimal intégrés');

        const devisTemp: Devis = {
          reference: id || `DEV-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`,
          client: clientId,
          date: new Date().toISOString(),
          articles: articlesDevis,
          groupesMatiere: [],
          coutMatiereTotal: 0,
          coutMainOeuvreTotal: 0,
          coutAccessoiresTotal: 0,
          coutPetitesFournitures: 0,
          montantTotal: calculerTotal()
        };
        
        // ✅ UN SEUL APPEL : Baseline + Optimal sont maintenant fusionnés dans le backend
        const devisOptimiseResultat = await apiFetch<any>('/devis/optimiser', {
          method: 'POST',
          headers: {
            'X-Premium-User': 'true'
          },
          body: JSON.stringify({ ...devisTemp, isPremium: true })
        });
        console.log('✅ [FRONTEND] Optimisation terminée (Baseline + Optimal fusionnés)');

        setDevisOptimise(devisOptimiseResultat);
        setOptimisationActive(true); // ✅ CORRECTION CRITIQUE: Mettre à jour l'état React

        if (isEditMode && devisExistant) {
          const clientIdToSave = finalisationData.clientId || clientId || devisExistant.client;
          const nouveauStatut = calculerStatutDevis();
          const economieCalculee = devisOptimiseResultat?.economieTotal ? Math.abs(Number(devisOptimiseResultat.economieTotal)) : 0;

          const devisAEnregistrer = {
            ...devisExistant,
            client: clientIdToSave,
            projet: finalisationData.nomProjet || devisExistant.projet,
            articles: articlesDevis,
            montantTotal: calculerTotal(),
            optimisation_active: true,  // ✅ CORRECTION: Forcer true car on vient d'optimiser
            economie_optimisation: economieCalculee,
            devisOptimise: devisOptimiseResultat,
            allowCalepinageUpdate: true,  // ✅ FLAG CRITIQUE pour persister devisOptimise en base
            remises: calculerMontantsRemisesPourPDF(),
            meta: {
              integrerEconomieCalepinage: integrerEconomieCalepinage
            },
            statut: nouveauStatut
          } as any;

          // Sauvegarder le devis avec le calepinage optimisé
          await devisApiService.saveDevis(devisAEnregistrer);
          console.log('💾 [FRONTEND] Devis sauvegardé avec calepinage optimisé');

          // Recharger le devis pour synchroniser l'état
          try {
            const devisRecharge = await devisApiService.getDevisById(devisExistant.id);
            setDevisExistant(devisRecharge as any);
            setDevisOptimise((devisRecharge as any)?.devisOptimise || devisOptimiseResultat);
            
            if ((devisRecharge as any)?.articles) {
              setArticlesDevis((devisRecharge as any).articles);
            }
            
            const optimisationFlag = (devisRecharge as any)?.optimisationActive ?? (devisRecharge as any)?.optimisation_active;
            if (optimisationFlag !== undefined) {
              setOptimisationActive(!!optimisationFlag);
            }
            
            const devisOptimiseFinal = (devisRecharge as any)?.devisOptimise || devisOptimiseResultat;
            setDevisInitial((prev: any) => ({
              ...(prev || {}),
              articles: (devisRecharge as any)?.articles || articlesDevis,
              optimisationActive: optimisationFlag ?? optimisationActive,
              devisOptimise: devisOptimiseFinal,
              finalisationData: prev?.finalisationData || finalisationData,
              remises: prev?.remises || remisesAppliquees
            }));
            
            console.log('✅ [FRONTEND] Devis rechargé et synchronisé');
          } catch (err) {
            console.error("❌ [FRONTEND] Erreur rechargement devis", err);
          }

          setDevisModifie(false);
          
          // Rafraîchir les vignettes
          await fetchVignettesBackend(devisExistant.id);
        } else {
          setDevisModifie(true);
        }
      } catch (error: any) {
        console.error("❌ [FRONTEND] Erreur optimisation:", error);
        setError(`Erreur lors de l'optimisation: ${error.message}`);
        setDevisOptimise(null);
      } finally {
        setIsOptimizing(false);
        setIsCalepinageRefreshing(false);
      }
    } else {
      setDevisOptimise(null);
    }
  };
  // Finaliser le devis
  const finaliserDevis = async () => {
    try {
      let devisAEnregistrer;
      
      if (isEditMode && devisExistant) {
        if (!devisExistant.projet) {
          const projet = window.prompt("Veuillez saisir le projet :");
          if (!projet?.trim()) {
            setError("Vous devez saisir un projet.");
            return;
          }
          devisExistant.projet = projet.trim();
        }
        
        devisAEnregistrer = {
          ...devisExistant,
          articles: articlesDevis,
          montantTotal: calculerTotal(),
          optimisationActive: optimisationActive,
          devisOptimise: devisOptimise,
          finalisationData: finalisationData
        } as any;
      } else {
        if (!clientId) {
          const selectClient = window.confirm("Aucun client associé. Sélectionner un client ?");
          if (selectClient) {
            navigate('/clients', { state: { returnToDevis: true, articlesDevis } });
            return;
          } else {
            setError("Un client doit être associé au devis.");
            return;
          }
        }
        
        const projet = window.prompt("Veuillez saisir le projet :");
        if (!projet?.trim()) {
          setError("Vous devez saisir un projet.");
          return;
        }
        
        devisAEnregistrer = {
          reference: `DEV-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`,
          client: clientId,
          date: new Date().toISOString(), // ✅ CORRECTION: Format ISO complet au lieu de split('T')[0]
          projet: projet.trim(),
          articles: articlesDevis,
          groupesMatiere: [],
          coutMatiereTotal: 0,
          coutMainOeuvreTotal: 0,
          coutAccessoiresTotal: 0,
          coutPetitesFournitures: 0,
          montantTotal: calculerTotal(),
          optimisationActive: optimisationActive,
          devisOptimise: devisOptimise
        } as any;
      }
      
      await devisApiService.saveDevis(devisAEnregistrer);
      
      if (isEditMode) {
        // En mode édition, rester sur la page et réinitialiser l'état de modification
        setConfirmationMessage('Devis mis à jour avec succès !');
        setTimeout(() => setConfirmationMessage(null), 3000);
        
        // Mettre à jour l'état initial avec les nouvelles données
        setDevisInitial({
          articles: articlesDevis,
          optimisationActive: optimisationActive,
          devisOptimise: devisOptimise
        });
        setDevisModifie(false);
      } else {
        // En mode création, rediriger vers la liste des devis
        alert('Devis enregistré avec succès !');
        setArticlesDevis([]);
        navigate('/devis');
      }
    } catch (error) {
      console.error("Erreur finalisation:", error);
      setError("Erreur lors de la finalisation");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
      );
    }
  
  const showSeededBanner = hasSeededCriticalEntreprise(entrepriseData?.seeded_fields, entrepriseData);
  
  // 🔍 DIAGNOSTIC: Logger la valeur calculée de showSeededBanner
  console.log('🔍 [DIAGNOSTIC] Calcul showSeededBanner:', {
    showSeededBanner,
    entrepriseData_exists: !!entrepriseData,
    seeded_fields: entrepriseData?.seeded_fields,
    nom: entrepriseData?.nom,
    soumisATVA: entrepriseData?.soumisATVA,
    tauxTVA: entrepriseData?.tauxTVA
  });
  
  const isArticleModalOpen = showArticleModal || showArticleLibreModal;
  
    return (
      <div className="space-y-6">
        {/* Message d'erreur flottant */}
        {error && (
          <div
            className={
              isArticleModalOpen
                ? 'fixed inset-0 z-[70] flex items-start justify-center pointer-events-none'
                : 'fixed top-4 right-4 z-50'
            }
          >
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg max-w-md w-full sm:w-auto pointer-events-auto mt-4 sm:mt-0">
              <div className="flex items-start">
                <span className="mr-2 mt-0.5">⚠️</span>
                <div className="flex-1">
                  <span className="font-medium">Erreur de validation</span>
                  <p className="text-sm mt-1">{error}</p>
                </div>
                <button
                  type="button"
                  className="ml-2 text-red-400 hover:text-red-600 transition-colors duration-200"
                  onClick={() => setError(null)}
                  title="Fermer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

      {/* En-tête */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditMode ? 'Voir/Modifier un devis' : 'Configurer un article'}
        </h1>
      </div>

      {/* Informations du devis - EN MODE ÉDITION UNIQUEMENT */}
      {isEditMode && devisExistant && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Informations du devis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Référence</label>
              <div className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">
                {devisExistant.reference}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">
                {devisExistant.date && !isNaN(new Date(devisExistant.date).getTime())
                  ? new Date(devisExistant.date).toLocaleDateString('fr-FR')
                  : 'Non définie'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">
                {clientInfo ? `${clientInfo.nom} ${clientInfo.prenom}` : devisExistant.client}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Projet</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">
                {finalisationData.nomProjet || devisExistant.projet || 'Non défini'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Commande(s) fournisseur</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">
                {Number(devisExistant.commandesFournisseurCount || 0) === 0 ? (
                  <span className="text-gray-400">Aucune</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const ref = devisExistant.reference;
                      const devisIdParam = (devisExistant as any).id || (devisExistant as any).devisId;
                      const params = new URLSearchParams();
                      const parsedId = Number(devisIdParam);
                      if (!Number.isNaN(parsedId) && Number.isFinite(parsedId)) {
                        params.set('devisId', String(parsedId));
                      } else if (ref) {
                        params.set('devisRef', ref);
                      }
                      const suffix = params.toString() ? `?${params.toString()}` : '';
                      navigate(`/commandes-fournisseur${suffix}`);
                    }}
                    className="text-primary-600 hover:text-primary-800 underline"
                  >
                    {`Voir (${devisExistant.commandesFournisseurCount})${
                      devisExistant.lastCommandeFournisseur
                        ? ` — Dernière : ${
                            devisExistant.lastCommandeFournisseur.numero_commande ||
                            (devisExistant.lastCommandeFournisseur.id || '').slice(0, 8)
                          } — ${devisExistant.lastCommandeFournisseur.statut}`
                        : ''
                    }`}
                  </button>
                )}
              </div>
              {devisExistant.commande_fournisseur_a_revalider && (
                <div className="mt-1 text-xs text-orange-600">
                  Devis modifié après la dernière commande (à revalider)
                </div>
              )}
            </div>
          </div>
          {devisExistant.description && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">
                {devisExistant.description}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alerte TVA - EN MODE ÉDITION UNIQUEMENT */}
      {isEditMode && devisExistant && entrepriseData && (
        <TvaStatusAlert
          devisSoumisTva={devisExistant.soumis_tva ?? true}
          devisTauxTva={devisExistant.taux_tva ?? 20.0}
          entrepriseSoumisTva={typeof entrepriseData?.soumisATVA === 'boolean' ? entrepriseData.soumisATVA : true}
          entrepriseTauxTva={typeof entrepriseData?.tauxTVA === 'number' ? entrepriseData.tauxTVA : 20.0}
          devisReference={devisExistant.reference}
          onConserver={handleConserverDevis}
          onDupliquer={async () => {
            if (!access || (access.access_mode !== 'active' && access.access_mode !== 'trial')) {
              window.dispatchEvent(
                new CustomEvent('access_denied', {
                  detail: { access_mode: access?.access_mode || 'preview', required: 'active' },
                })
              );
              await startTrial().catch(() => {});
              return;
            }
            try {
              if (!devisExistant?.id || !entrepriseData) {
                setError('Impossible de dupliquer : données manquantes');
                return;
              }

              setLoading(true);
              
              // ÉTAPE 1 : Recalculer tous les prix des articles avec les tarifs actuels

              const articlesRecalcules: Article[] = [];
              
            for (const articleExistant of articlesDevis) {
              if (articleExistant.prixCalcule) {
                try {
                  // ✅ CORRECTION CRITIQUE : Nettoyer TOUS les champs calculés avant envoi au backend
                  const { 
                    prixCalcule: _, 
                    prixUnitaire: __, 
                    id: ___,  // Supprimer l'ID pour forcer un nouveau calcul
                    nomTechnique: ____,  // Sera régénéré
                    ...articleSansCalcul 
                  } = articleExistant;
                  
                  const articleToSend = {
                    ...articleSansCalcul,
                    dimensions: {
                      ...articleExistant.dimensions,
                      talon: Number(articleExistant.dimensions.talon)
                    },
                    prixUnitaire: 0 // Valeur par défaut pour satisfaire le type
                  };

                  // Recalculer le prix avec les tarifs actuels
                  const resultatCalcul = await apiService.calculerPrix(articleToSend);
                    const prixTotalArticle = resultatCalcul.prixTotalArticle || resultatCalcul.prix;
                    const prixUnitaireRecalcule = resultatCalcul.prixUnitaire ?? (prixTotalArticle / articleExistant.quantite);
                    
                    // Mettre à jour l'article avec le nouveau prix
                    const articleMisAJour: Article = {
                      ...articleExistant,
                      prixUnitaire: prixUnitaireRecalcule,
                      prixCalcule: resultatCalcul
                    };
                    
                    articlesRecalcules.push(articleMisAJour);
                    console.log(`✅ [Duplication] Article "${articleExistant.nom}" recalculé : ${prixUnitaireRecalcule.toFixed(2)}€`);
                  } catch (error) {
                    console.error(`❌ [Duplication] Erreur recalcul article ${articleExistant.nom}:`, error);
                    // Garder l'article avec l'ancien prix en cas d'erreur
                    articlesRecalcules.push(articleExistant);
                  }
                } else {
                  // Article libre sans calcul, garder tel quel
                  articlesRecalcules.push(articleExistant);
                }
              }

              // ÉTAPE 2 : Créer une copie du devis avec le nouveau régime TVA et les articles recalculés
              
              // 🔍 LOGS DE DIAGNOSTIC TVA






              const devisDuplique = {
                ...devisExistant,
                id: undefined, // Nouveau devis = nouvel ID
                reference: undefined, // Nouvelle référence sera générée
                date: new Date().toISOString(),
                soumis_tva: typeof entrepriseData?.soumisATVA === 'boolean' ? entrepriseData.soumisATVA : true,
                taux_tva: typeof entrepriseData?.tauxTVA === 'number' ? entrepriseData.tauxTVA : 20.0,
                articles: articlesRecalcules, // ✅ Articles avec prix recalculés
                statut: 'BROUILLON',
                pdf_genere_le: null,
                marque_comme_accepte: false,
                marque_comme_livre: false
              };

              // Recalculer le montant total
              devisDuplique.montantTotal = articlesRecalcules.reduce(
                (total, article) => total + article.prixUnitaire * article.quantite, 
                0
              );
              
              console.log(`🔄 [Duplication] Montant total recalculé : ${devisDuplique.montantTotal.toFixed(2)}€`);

              // ÉTAPE 3 : Sauvegarder le nouveau devis
              const nouveauDevis = await devisApiService.saveDevis(devisDuplique);

              // ÉTAPE 4 : Réinitialiser le mode conservé et rediriger
              setModeConserve(false);
              navigate(`/devis/configurateur/${nouveauDevis.id}`);
              
              setConfirmationMessage(
                `✅ Devis dupliqué avec succès ! 
                Nouvelle référence : ${nouveauDevis.reference}
                Tous les prix ont été recalculés avec les tarifs actuels.`
              );
              setTimeout(() => setConfirmationMessage(null), 8000);
            } catch (error: any) {
              console.error('❌ [Duplication] Erreur:', error);
              setError(`Erreur lors de la duplication : ${error.message || 'Erreur inconnue'}`);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {/* Bandeau mode conservé */}
      {modeConserve && (
        <BandeauModeConserve raison="Devis conservé avec son régime TVA d'origine" />
      )}

      {/* Alerte de tarifs - EN MODE ÉDITION UNIQUEMENT */}
      {isEditMode && devisExistant && hasModifications && showTarifsAlert && (
        <TarifsAlert
          devis={devisExistant}
          articles={articlesDevis}
          modifications={modifications}
          onUpdateTarifs={(updatedArticles) => {
            setArticlesDevis([...updatedArticles]);
            setRenderKey(prev => prev + 1);
            
            // ✅ CORRECTION : Utiliser un timestamp complet ISO au lieu d'une date formatée
            const nouvelleDateModification = new Date().toISOString();
            
            setDevisExistant((prev: any) => ({ 
              ...prev, 
              dateDerniereModification: nouvelleDateModification
            }));

            // Marquer l'alerte comme traitée et masquer l'alerte
            setTarifsAlertProcessed(true);
            setShowTarifsAlert(false);
            
            // Marquer le devis comme modifié
            setDevisModifie(true);

            // Afficher un message de confirmation
            setConfirmationMessage('Tarifs mis à jour avec succès ! Les prix ont été recalculés.');
            setTimeout(() => setConfirmationMessage(null), 5000);
          }}
          onIgnore={() => {

            setTarifsAlertProcessed(true);
            setShowTarifsAlert(false);
          }}
        />
      )}

      {confirmationMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {confirmationMessage}
        </div>
      )}

      {/* Formulaire de configuration dans une modale */}
      {showArticleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-8">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={fermerArticleModal}
          ></div>
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isEditingArticle ? 'Modifier un article' : 'Nouvel article'}
                </h2>
                <p className="text-sm text-gray-600">Configuration et pricing dans la modale</p>
              </div>
              <button
                type="button"
                onClick={fermerArticleModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-full p-2"
                aria-label="Fermer la modale de configuration d'article"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {prixCalcule && donneesModifiees && (
                <div className="bg-amber-100 border border-amber-400 text-amber-700 px-4 py-3 rounded relative">
                  <div className="flex items-center">
                    <span className="mr-2">⚠️</span>
                    <span>Données modifiées. Recalculez le prix avant d'ajouter au devis.</span>
                  </div>
                </div>
              )}

              <ArticleConfigurationCompact 
                article={article} 
                matieres={matieres} 
                handleChange={handleChange}
                onDataChange={handleDataChange}
              />
              
              <Materiaux 
                article={article} 
                matieres={matieres} 
                handleChange={handleChange}
                onDataChange={handleDataChange}
                onMatieresRefresh={setMatieres}
              />

              {prixCalcule && (
                <DetailsPrix 
                  prixCalcule={prixCalcule} 
                  showDetailsPrix={showDetailsPrix} 
                  setShowDetailsPrix={setShowDetailsPrix}
                  quantite={article.quantite} 
                />
              )}
            </div>

            <div className="border-t bg-white px-6 py-4">
              <ActionButtons
                calculating={calculating}
                prixCalcule={prixCalcule}
                donneesModifiees={donneesModifiees}
                isEditingArticle={isEditingArticle}
                error={error}
                onReinitialiser={reinitialiserFormulaire}
                onCalculerPrix={calculerPrix}
                onAjouterAuDevis={ajouterAuDevis}
                onMettreAJourArticle={mettreAJourArticle}
              />
            </div>
          </div>
        </div>
      )}

      {/* Section Articles du devis - TOUJOURS AFFICHÉE */}
      <div id="tableau-articles" className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Articles du devis</h2>
          <div className="flex space-x-2">
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
                modeConserve
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 bg-white hover:bg-gray-50'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200`}
              onClick={modeConserve ? undefined : () => {
                setArticleLibreEnEdition(null);
                setShowArticleLibreModal(true);
              }}
              disabled={modeConserve}
              title={modeConserve ? 'Action bloquée : devis en mode lecture seule' : 'Ajouter un article personnalisé au devis'}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Ajouter article libre
            </button>
            
            {/* Bouton "Ajouter un article" - visible seulement quand il y a des articles */}
            {articlesDevis.length > 0 && (
              <button
                type="button"
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  modeConserve 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200`}
                onClick={modeConserve ? undefined : ajouterNouvelArticle}
                disabled={modeConserve}
                title={modeConserve ? 'Action bloquée : devis en mode lecture seule' : 'Ajouter un nouvel article configuré'}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter un article
              </button>
            )}
          </div>
        </div>

        {articlesDevis.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix unitaire</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" key={renderKey}>
                  {articlesDevis.map((article) => (
                    <tr key={article.id}>
                      <td className="px-6 py-4" style={{ maxWidth: '1000px', whiteSpace: 'normal' }}>
                        <div className="flex flex-col items-start space-y-1">
                          <div 
                            className="text-sm font-medium text-gray-900" 
                            title={article.nom}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            {article.nom}
                          </div>
                          {(article as any).estArticleLibre && (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                              <span>Libre</span>
                              <span className="text-gray-500">·</span>
                              <span>
                                {((article as any).typeArticleLibre === 'main_oeuvre' && 'MO') ||
                                  ((article as any).typeArticleLibre === 'sous_traitance' && 'Sous-tr') ||
                                  'Matière'}
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4" style={{ maxWidth: '1000px', whiteSpace: 'normal' }}>
                        {article.prixCalcule ? (
                          <div 
                            className="text-sm text-gray-500 hover:text-blue-600 cursor-pointer transition-colors duration-200"
                            onClick={() => afficherDetailsPrix(article)}
                            title={article.nomTechnique || "Détails du prix de cet article"}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            {article.nomTechnique}
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-gray-500"
                            title={article.nomTechnique}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            {article.nomTechnique}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{article.quantite}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{article.prixUnitaire.toFixed(2)} €</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{(article.prixUnitaire * article.quantite).toFixed(2)} €</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            type="button"
                            className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white ${
                              modeConserve
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700'
                            } transition-colors duration-200`}
                            onClick={modeConserve ? undefined : () => modifierArticle(article)}
                            disabled={modeConserve}
                            title={modeConserve ? 'Action bloquée : devis en mode lecture seule' : 'Modifier cet article'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </button>
                          <button
                            type="button"
                            className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white ${
                              modeConserve
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700'
                            } transition-colors duration-200`}
                            onClick={modeConserve ? undefined : () => {
                              if (window.confirm(`Supprimer l'article "${article.nom}" ?`)) {
                                supprimerArticle(article.id || '');
                              }
                            }}
                            disabled={modeConserve}
                            title={modeConserve ? 'Action bloquée : devis en mode lecture seule' : 'Supprimer cet article'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
        </div>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Lignes de remise */}
                  {(() => {
                    const montantRemises = calculerMontantRemises();
                    if (montantRemises > 0.01) {
                      if (remisesAppliquees.typeRemise === 'globale') {
                        return (
                          <tr className="bg-orange-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-orange-700">Remise commerciale</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-orange-600">Remise globale de {remisesAppliquees.globale}% sur le total du devis</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-orange-700">1</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-orange-700">-{montantRemises.toFixed(2)} €</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-orange-700">-{montantRemises.toFixed(2)} €</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <button
                                type="button"
                                className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 transition-colors duration-200"
                                onClick={() => {
                                  setRemisesAppliquees({ mainOeuvre: 0, matieresFournitures: 0, globale: 0, typeRemise: 'detaillee' });
                                  setDevisModifie(true);
                                  setConfirmationMessage('Remises supprimées avec succès !');
                                  setTimeout(() => setConfirmationMessage(null), 3000);
                                }}
                                title="Supprimer cette remise"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        );
                      } else {
                        // Remises détaillées
                        const lignesRemise = [];
                        
                        // Calculer les montants par catégorie
                         let coutMainOeuvre = 0;
                        let coutMatieresFournitures = 0;

                        articlesDevis.forEach(article => {
                          const quantite = article.quantite || 1;
                          if ((article as any).estArticleLibre) {
                            const typeArticleLibre = (article as any).typeArticleLibre;
                            const montant = article.prixUnitaire * quantite;
                            if (typeArticleLibre === 'matiere' || typeArticleLibre === 'sous_traitance') {
                              coutMatieresFournitures += montant;
                            } else if (typeArticleLibre === 'main_oeuvre') {
                              coutMainOeuvre += montant;
                            }
                          } else if (article.prixCalcule?.details?.couts) {
                            const couts = article.prixCalcule.details.couts;
                            coutMainOeuvre += (couts.main_oeuvre || 0);
                            coutMatieresFournitures += (couts.matieres || 0) + (couts.accessoires || 0);
                          } else {
                            const montantTotal = article.prixUnitaire * quantite;
                            coutMainOeuvre += montantTotal;
                          }
                        });

                        // Remise sur Main d'œuvre
                        if (remisesAppliquees.mainOeuvre > 0) {
                          const montantRemiseMO = coutMainOeuvre * remisesAppliquees.mainOeuvre / 100;
                          lignesRemise.push(
                            <tr key="remise-mo" className="bg-blue-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-blue-700">Remise Main d'œuvre</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-blue-600">Remise de {remisesAppliquees.mainOeuvre}% sur la main d'œuvre</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-blue-700">1</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-blue-700">-{montantRemiseMO.toFixed(2)} €</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-blue-700">-{montantRemiseMO.toFixed(2)} €</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <button
                                  type="button"
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors duration-200"
                                  onClick={() => {
                                    setRemisesAppliquees(prev => ({ ...prev, mainOeuvre: 0 }));
                                    setDevisModifie(true);
                                    setConfirmationMessage('Remise Main d\'œuvre supprimée !');
                                    setTimeout(() => setConfirmationMessage(null), 3000);
                                  }}
                                  title="Supprimer cette remise"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        // Remise sur Matières et fournitures
                        if (remisesAppliquees.matieresFournitures > 0) {
                          const montantRemiseMatFour = coutMatieresFournitures * remisesAppliquees.matieresFournitures / 100;
                          lignesRemise.push(
                            <tr key="remise-mat-four" className="bg-green-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-green-700">Remise Matières et fournitures</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-green-600">Remise de {remisesAppliquees.matieresFournitures}% sur les matières et fournitures</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-green-700">1</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-green-700">-{montantRemiseMatFour.toFixed(2)} €</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-green-700">-{montantRemiseMatFour.toFixed(2)} €</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <button
                                  type="button"
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 transition-colors duration-200"
                                  onClick={() => {
                                    setRemisesAppliquees(prev => ({ ...prev, matieresFournitures: 0 }));
                                    setDevisModifie(true);
                                    setConfirmationMessage('Remise Matières et fournitures supprimée !');
                                    setTimeout(() => setConfirmationMessage(null), 3000);
                                  }}
                                  title="Supprimer cette remise"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        return lignesRemise;
                      }
                    }
                    return null;
                  })()}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end">
              <div className="text-right">
                <div className="text-sm text-gray-500">
                  {entrepriseData?.soumisATVA ? 'Total HT' : 'Total'}
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {calculerTotal().toFixed(2)} €
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-lg font-medium text-gray-900 mb-2">Aucun article dans le devis</p>
            <p className="text-sm text-gray-500 mb-4">
              Commencez par ajouter un article libre pour composer votre devis.
            </p>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
              onClick={ajouterNouvelArticle}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Configurer un article
            </button>
          </div>
        )}

        {showSeededBanner && (
          <div
            className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded flex flex-col gap-2"
            data-testid="seeded-params-banner"
          >
            <div className="flex items-center justify-between gap-3">
              <span>Ce devis est calculé avec des paramètres par défaut non confirmés.</span>
              <button
                type="button"
                onClick={() => setShowParamsPanel((prev) => !prev)}
                className="px-3 py-1 text-sm font-semibold bg-white border border-amber-300 rounded hover:bg-amber-100"
              >
                Voir les paramètres utilisés
              </button>
            </div>
            {showParamsPanel && (
              <div className="bg-white border border-amber-200 rounded p-3 space-y-2">
                <div className="text-sm font-semibold text-amber-800">Champs critiques Entreprise</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {getCriticalEntrepriseFieldsStatus(entrepriseData?.seeded_fields, entrepriseData).map((f) => (
                    <div key={f.key} className="text-sm">
                      <div className="font-medium text-gray-800">{f.label}</div>
                      <div className="text-gray-700">{f.value ?? ''}</div>
                      <div className={`text-xs font-semibold ${f.seeded ? 'text-amber-700' : 'text-green-700'}`}>
                        {f.seeded ? 'Non confirmé (par défaut)' : 'Confirmé'}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/entreprise')}
                    className="text-sm text-blue-700 hover:underline"
                  >
                    Aller à Entreprise
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/parametres/tarifs')}
                    className="text-sm text-blue-700 hover:underline"
                  >
                    Aller à Tarifs & Coefficients
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <ArticleLibreModal 
          isOpen={showArticleLibreModal}
          initialArticle={articleLibreEnEdition || undefined}
          mode={articleLibreEnEdition ? 'edit' : 'create'}
          onClose={() => {
            setShowArticleLibreModal(false);
            setArticleLibreEnEdition(null);
          }}
          onSave={(articleLibre) => {
            setArticlesDevis(prev => {
              if (articleLibreEnEdition) {
                return prev.map(a => a.id === articleLibreEnEdition.id ? { ...a, ...articleLibre } : a);
              }
              return [...prev, articleLibre];
            });
            setDevisModifie(true);
            setShowArticleLibreModal(false);
            setArticleLibreEnEdition(null);
          }}
        />
        
        {/* Modal pour afficher les détails du prix d'un article */}
        {showDetailsPrixModal && articleDetailsPrix && articleDetailsPrix.prixCalcule && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                {/* En-tête de la modal */}
                <div className="flex items-center justify-between pb-3 border-b">
                  <h3 className="text-lg font-medium text-gray-900">
                    Détails du prix - {articleDetailsPrix.nom}
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setShowDetailsPrixModal(false)}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Contenu de la modal */}
                <div className="mt-4">
                  <DetailsPrix 
                    prixCalcule={articleDetailsPrix.prixCalcule} 
                    showDetailsPrix={true}
                    setShowDetailsPrix={() => {}} // Pas besoin de toggle dans la modal
                    quantite={articleDetailsPrix.quantite} 
                  />
                </div>
                
                {/* Pied de modal */}
                <div className="flex justify-end pt-4 border-t mt-4">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200"
                    onClick={() => setShowDetailsPrixModal(false)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Bloc Frais & livraison */}
      {articlesDevis.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 border border-gray-200 mb-4">
          {(() => {
            const pf = (finalisationData as any)?.petitesFournitures;
            const pfActif = pf?.inclure;
            const pfMontant = pfActif ? (pf?.montantCalcule || 0) : 0;
            const pfLabel = pfActif ? `${pfMontant.toFixed(2)} €` : '— (non applicable)';

            const fd = (finalisationData as any)?.fraisDeplacementConfig;
            const dfMontant = fd?.montantCalcule || 0;
            const dfComplet = !!(fd && fd.distance && fd.tarifKm !== undefined);
            const dfInclus = dfComplet && dfMontant > 0;
            const dfLabel = dfInclus ? `${dfMontant.toFixed(2)} €` : '—';
            const dfState = dfInclus ? 'Inclus' : (dfComplet ? '⚠ Non inclus' : '⚠ Paramètres manquants');

            const fp = (finalisationData as any)?.fraisPort || {};
            const fpStatut = (fp.statut || 'INCONNU').toUpperCase();
            const fpMontant = fp.montant || 0;
            const fpInclus = fpStatut !== 'INCONNU' && fpMontant > 0;
            const fpLabel = fpInclus ? `${fpMontant.toFixed(2)} €` : '—';
            const fpState = fpStatut === 'INCONNU' ? '⚠ FP non inclus' : `${fpStatut === 'ESTIME' ? 'Estimé' : 'Confirmé'} (inclus)`;

            const showBanner = (dfComplet && !dfInclus) || fpStatut === 'INCONNU';

            return (
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Frais & livraison</h2>
                  <div className="text-sm text-gray-600">PF • DF • FP</div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Petites fournitures</span>
                      <span className="text-sm font-medium text-gray-900">{pfLabel}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Frais de déplacement</span>
                      <span className="text-sm font-medium text-gray-900">{dfLabel} <span className="text-xs text-gray-500">({dfState})</span></span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Frais de port</span>
                      <span className="text-sm font-medium text-gray-900">{fpLabel} <span className="text-xs text-gray-500">({fpState})</span></span>
                    </div>
                  </div>

                  {showBanner && (
                    <div className="mt-4 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                      ⚠ Certains frais ne sont pas inclus dans le total (cliquez pour compléter)
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  onClick={() => {
                    setFinalisationMode('fees');
                    setShowFinalisationModal(true);
                  }}
                >
                  Renseigner
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* 🔴 BLOC RÉCAPITULATIF FINANCIER 🔴 */}
      {articlesDevis.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg rounded-lg p-6 border border-blue-200">
          <h2 className="text-lg font-bold mb-4 text-indigo-900 flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            💰 Récapitulatif financier
          </h2>
          <p className="text-sm text-indigo-900 mb-3">
            Le récapitulatif est calculé automatiquement selon les données et paramétrages enregistrés. Contrôlez les montants avant transmission au client final.
          </p>
          
          {(() => {
            const montantArticles = articlesDevis.reduce((total, article) => 
              total + (article.prixUnitaire * article.quantite), 0
            );
            
            const economie = (hasPremiumFeatures && devisOptimise && optimisationActive) ? 
              Math.abs(devisOptimise.economieTotal || 0) : 0;
            const montantApresOptimisation = montantArticles - (integrerEconomieCalepinage ? economie : 0);
            
            const finalisationDataAny = finalisationData as any;
            const montantPF = finalisationDataAny?.petitesFournitures?.montantCalcule || 0;
            const montantDeplacement = finalisationDataAny?.fraisDeplacementConfig?.montantCalcule || 0;
            const fraisPort = finalisationDataAny?.fraisPort || {};
            const fpStatut = (fraisPort?.statut || 'INCONNU').toUpperCase();
            const montantFP = fpStatut !== 'INCONNU' ? (fraisPort?.montant || 0) : 0;
            
            const totalHT = montantApresOptimisation + montantPF + montantDeplacement + montantFP;
            
            const soumisATVA = entrepriseData?.soumisATVA ?? true;
            const tauxTVA = devisExistant?.taux_tva || entrepriseData?.tauxTVA || 20;
            const montantTVA = soumisATVA ? (totalHT * tauxTVA / 100) : 0;
            const totalTTC = totalHT + montantTVA;
            
            return (
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-blue-200">
                  <span className="text-sm font-medium text-gray-700">Articles du devis</span>
                  <span className="text-base font-semibold text-gray-900">{montantArticles.toFixed(2)} €</span>
                </div>
                
                {/* Afficher la ligne "Économie optimisation" seulement si :
                    - economie > 0 ET
                    - (optimisation désactivée OU "Intégrer l'économie au prix client" activé)
                    Autrement dit : Masquer si optimisationActive === true ET integrerEconomieCalepinage === false */}
                {economie > 0 && (!optimisationActive || integrerEconomieCalepinage) && (
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-sm font-medium text-green-700">Économie optimisation</span>
                    <span className="text-base font-semibold text-green-700">- {economie.toFixed(2)} €</span>
                  </div>
                )}
                
                {montantPF > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-sm font-medium text-gray-700">+ Petites fournitures ({finalisationDataAny?.petitesFournitures?.taux || 5}%)</span>
                    <span className="text-base font-semibold text-gray-900">{montantPF.toFixed(2)} €</span>
                  </div>
                )}
                
                {montantDeplacement > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-sm font-medium text-gray-700">+ Frais de déplacement</span>
                    <span className="text-base font-semibold text-gray-900">{montantDeplacement.toFixed(2)} €</span>
                  </div>
                )}

                {montantFP > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-blue-200">
                    <span className="text-sm font-medium text-gray-700">
                      + Frais de port {fpStatut === 'ESTIME' ? '(estimé)' : fpStatut === 'CONFIRME' ? '(confirmé)' : ''}
                    </span>
                    <span className="text-base font-semibold text-gray-900">{montantFP.toFixed(2)} €</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center py-3 bg-indigo-100 px-4 rounded-lg border-2 border-indigo-300">
                  <span className="text-base font-bold text-indigo-900">= TOTAL HT</span>
                  <span className="text-xl font-bold text-indigo-900">{totalHT.toFixed(2)} €</span>
                </div>

                {fpStatut === 'INCONNU' && (
                  <div className="mt-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                    ⚠ Total affiché hors frais de port
                  </div>
                )}
                
                {soumisATVA && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-blue-200">
                      <span className="text-sm font-medium text-gray-700">+ TVA ({tauxTVA}%)</span>
                      <span className="text-base font-semibold text-gray-900">{montantTVA.toFixed(2)} €</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 bg-indigo-200 px-4 rounded-lg border-2 border-indigo-400">
                      <span className="text-base font-bold text-indigo-900">= TOTAL TTC</span>
                      <span className="text-xl font-bold text-indigo-900">{totalTTC.toFixed(2)} €</span>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Section Détail des pièces - RÉSUMÉ COMPACT */}
      {articlesDevis.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium">Détail des pièces</h2>
              <div className="text-sm text-gray-600 mt-1">
                {(() => {
                  // Compter seulement les articles configurés (pas les articles libres)
                  const articlesConfigures = articlesDevis.filter(article => !(article as any).estArticleLibre);
                  const nombrePieces = articlesConfigures.reduce((sum, a) => sum + (a.quantite || 1), 0);
                  
                  return (
                    <>
                      <span className="font-medium">
                        {articlesConfigures.length} article{articlesConfigures.length > 1 ? 's' : ''} décomposé{articlesConfigures.length > 1 ? 's' : ''} en {nombrePiecesCalculees > 0 ? nombrePiecesCalculees : nombrePieces} pièce{(nombrePiecesCalculees > 0 ? nombrePiecesCalculees : nombrePieces) > 1 ? 's' : ''}
                      </span>
                      <span className="mx-2">•</span>
                      <span>Matières et quantités détaillées</span>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                onClick={() => setShowDetailPiecesModal(true)}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Voir le détail
              </button>
              {id && devisExistant?.reference && (
                <ExportButtons
                  devisId={id}
                  devisReference={devisExistant.reference}
                  size="sm"
                  variant="secondary"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modale pour le détail des pièces - HAUTEUR PLEIN ÉCRAN */}
      {showDetailPiecesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-full max-h-none flex flex-col">
            {/* En-tête de la modal */}
            <div className="flex items-center justify-between p-6 border-b bg-gray-50 rounded-t-lg">
              <h3 className="text-2xl font-semibold text-gray-900">
                Détail des pièces et matières
              </h3>
              <div className="flex items-center space-x-4">
                {id && devisExistant?.reference && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">Export atelier :</span>
                    <ExportButtons
                      devisId={id}
                      devisReference={devisExistant.reference}
                      size="sm"
                      variant="primary"
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-200 rounded-full"
                  onClick={() => setShowDetailPiecesModal(false)}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Contenu de la modal - FLEXIBLE */}
            <div className="flex-1 overflow-auto p-6">
              <DetailPieces 
                articles={articlesDevis} 
                matieres={matieres} 
                devisId={id}
                devisReference={devisExistant?.reference}
                onPiecesCalculated={setNombrePiecesCalculees}
                devisOptimise={devisOptimise}
                optimisationActive={optimisationActive}
                onDevisOptimiseUpdate={async (newDevisOptimise) => {
                  console.log('📥 [Parent] Mise à jour devisOptimise depuis enfant:', newDevisOptimise);
                  
                  // Mettre à jour l'état local
                  setDevisOptimise(newDevisOptimise);
                  
                  // ✅ CRITIQUE : Marquer le devis comme modifié pour déclencher la sauvegarde automatique
                  setDevisModifie(true);
                  
                  console.log('✅ [Parent] DevisOptimise mis à jour, sauvegarde automatique dans 2s...');
                }}
              />
            </div>
            
            {/* Pied de modal */}
            <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 font-medium"
                onClick={() => setShowDetailPiecesModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Détail des coûts - RÉSUMÉ COMPACT */}
      {articlesDevis.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className="text-lg font-medium mb-3">Détail des coûts</h2>
              
              {/* Barre segmentée unique */}
              <div className="space-y-3">
                {(() => {
                  // Calculer les coûts par catégorie
                  let coutMainOeuvre = 0;
                  let coutMatieres = 0;
                  let coutAccessoires = 0;
                  let coutSousTraitance = 0;

                  articlesDevis.forEach(article => {
                    const quantite = article.quantite || 1;
                    if ((article as any).estArticleLibre) {
                      const typeArticleLibre = (article as any).typeArticleLibre;
                      const montant = article.prixUnitaire * quantite;
                      if (typeArticleLibre === 'matiere') {
                        coutMatieres += montant;
                      } else if (typeArticleLibre === 'sous_traitance') {
                        coutSousTraitance += montant;
                      } else if (typeArticleLibre === 'main_oeuvre') {
                        coutMainOeuvre += montant;
                      }
                    } else if (article.prixCalcule?.details?.couts) {
                      const couts = article.prixCalcule.details.couts;
                      coutMainOeuvre += (couts.main_oeuvre || 0);
                      coutMatieres += (couts.matieres || 0);
                      coutAccessoires += (couts.accessoires || 0);
                    } else {
                      const montantTotal = article.prixUnitaire * quantite;
                      coutMainOeuvre += montantTotal;
                    }
                  });

                  const totalSansEconomie = coutMainOeuvre + coutMatieres + coutAccessoires + coutSousTraitance;
                  const categories = [
                    { nom: 'Main d\'œuvre', montant: coutMainOeuvre, couleur: 'bg-blue-500', couleurTexte: 'text-blue-700' },
                    { nom: 'Matières', montant: coutMatieres, couleur: 'bg-green-500', couleurTexte: 'text-green-700' },
                    { nom: 'Accessoires', montant: coutAccessoires, couleur: 'bg-yellow-500', couleurTexte: 'text-yellow-700' },
                    ...(coutSousTraitance > 0 ? [{ nom: 'Sous-traitance', montant: coutSousTraitance, couleur: 'bg-purple-500', couleurTexte: 'text-purple-700' }] : [])
                  ].filter(cat => cat.montant > 0);

                  return (
                    <>
                      {/* Barre segmentée */}
                      <div className="flex bg-gray-200 rounded-full h-6 overflow-hidden">
                        {categories.map((categorie, index) => {
                          const pourcentage = totalSansEconomie > 0 ? (categorie.montant / totalSansEconomie) * 100 : 0;
                          return (
                            <div
                              key={index}
                              className={`${categorie.couleur} transition-all duration-300 flex items-center justify-center`}
                              style={{ width: `${pourcentage}%` }}
                              title={`${categorie.nom}: ${categorie.montant.toFixed(0)}€ (${pourcentage.toFixed(0)}%)`}
                            >
                              {pourcentage > 15 && (
                                <span className="text-xs font-medium text-white">
                                  {pourcentage.toFixed(0)}%
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Légende */}
                      <div className="flex flex-wrap gap-4 justify-center">
                        {categories.map((categorie, index) => {
                          const pourcentage = totalSansEconomie > 0 ? (categorie.montant / totalSansEconomie) * 100 : 0;
                          return (
                            <div key={index} className="flex items-center space-x-1">
                              <div className={`w-3 h-3 ${categorie.couleur} rounded-full`}></div>
                              <span className={`text-xs font-medium ${categorie.couleurTexte}`}>
                                {categorie.nom}
                              </span>
                              <span className="text-xs text-gray-600">
                                ({pourcentage.toFixed(0)}% • {categorie.montant.toFixed(0)}€)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
              
              <div className="text-xs text-gray-500 mt-2">
                Total: {calculerTotal().toFixed(0)} €
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 ml-4"
              onClick={() => setShowDetailCoutsModal(true)}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Voir le détail
            </button>
          </div>
        </div>
      )}

      {/* Modale pour le détail des coûts - HAUTEUR PLEIN ÉCRAN */}
      {showDetailCoutsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-full max-h-none flex flex-col">
            {/* En-tête de la modal */}
            <div className="flex items-center justify-between p-6 border-b bg-gray-50 rounded-t-lg">
              <h3 className="text-2xl font-semibold text-gray-900">
                Détail des coûts par catégorie
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-200 rounded-full"
                onClick={() => setShowDetailCoutsModal(false)}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Contenu de la modal - FLEXIBLE */}
            <div className="flex-1 overflow-auto p-6">
              <DetailCouts 
                articles={articlesDevis} 
                economieOptimisation={
                  hasPremiumFeatures && devisOptimise && optimisationActive && devisOptimise.economieTotal !== undefined 
                    ? Math.abs(Number(devisOptimise.economieTotal)) 
                    : 0
                }
                integrerEconomieCalepinage={integrerEconomieCalepinage}
                remisesAppliquees={remisesAppliquees}
                onAppliquerRemises={handleAppliquerRemises}
              />
            </div>
            
            {/* Pied de modal */}
            <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 font-medium"
                onClick={() => setShowDetailCoutsModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Optimisation du calepinage - RÉSUMÉ COMPACT */}
      {articlesDevis.length > 0 && (
        <OptimisationCompact
          optimisationActive={optimisationActive}
          devisOptimise={devisOptimise}
          hasPremiumFeatures={hasPremiumFeatures}
          integrerEconomieCalepinage={integrerEconomieCalepinage}
          justInvalidated={justInvalidated} // ✅ NOUVEAU: Passer le flag pour afficher l'alerte
          onOptimiser={() => {
            setOptimisationActive(true);
            setJustInvalidated(false); // ✅ Réinitialiser le flag quand on réoptimise
            setDevisModifie(true); // ✅ CORRECTION: Marquer comme modifié pour déclencher sauvegarde
            setTimeout(() => optimiserDevis(true), 100);
          }}
          onDesactiver={() => {
            setOptimisationActive(false);
            setDevisOptimise(null);
            setJustInvalidated(false); // ✅ Réinitialiser le flag quand on désactive
            setDevisModifie(true); // ✅ CORRECTION: Marquer comme modifié pour déclencher sauvegarde
          }}
          onVoirDetail={() => setShowOptimisationModal(true)}
          onToggleIntegrerEconomie={async (value: boolean) => {
            setIntegrerEconomieCalepinage(value);
            setDevisModifie(true);
            
            // Persister immédiatement le choix dans le devis
            if (isEditMode && devisExistant?.id) {
              try {
                const devisAMettreAJour = {
                  ...devisExistant,
                  meta: {
                    ...(devisExistant.meta || {}),
                    integrerEconomieCalepinage: value
                  }
                };
                
                await devisApiService.saveDevis(devisAMettreAJour);
                
                setConfirmationMessage(
                  value 
                    ? "L'économie sera déduite du prix client"
                    : "L'économie sera conservée en marge"
                );
                setTimeout(() => setConfirmationMessage(null), 3000);
              } catch (error) {
                console.error("Erreur lors de la mise à jour du flag:", error);
                setError("Erreur lors de la sauvegarde du paramètre");
              }
            }
          }}
        />
      )}

      {/* Modale pour le détail de l'optimisation du calepinage */}
      <OptimisationModal
        isOpen={showOptimisationModal}
        devisOptimise={devisOptimise}
        matieres={matieres}
        isRefreshing={isCalepinageRefreshing || isOptimizing}
        devisId={id}  // ✅ CORRECTION: Passer devisId pour charger vignettes dans la modale
        onClose={() => setShowOptimisationModal(false)}
        onRecalculer={() => {
          setOptimisationActive(true);
          setTimeout(() => optimiserDevis(true), 100);
        }}
        onDesactiver={() => {
          setOptimisationActive(false);
          setDevisOptimise(null);
        }}
      />

      {/* Section Finalisation du devis - RÉSUMÉ COMPACT */}
      {articlesDevis.length > 0 && (
      <FinalisationCompact
          finalisationData={finalisationData}
          clientInfo={clientInfo}
          devisExistant={devisExistant}
          modeConserve={modeConserve}
          onVoirDetail={() => {
            setFinalisationMode('full');
            setShowFinalisationModal(true);
          }}
          hasCgv={!!entrepriseData?.has_cgv}
          inclureCgv={inclureCgv}
          onToggleCgv={setInclureCgv}
          onGenererPDF={async () => {
            if (!isEditMode || !devisExistant?.id) {
              setError('Impossible de générer le PDF : devis non sauvegardé');
              return;
            }

            // Vérifier qu'il y a au moins des articles
            if (articlesDevis.length === 0) {
              setError('Le devis doit contenir au moins un article pour générer un PDF.');
              return;
            }

            try {
              setLoading(true);

              const result = await ExportService.generateDevisPDF(devisExistant.id, inclureCgv && !!entrepriseData?.has_cgv);

              if (result.success && result.pdfUrl) {
                // Télécharger automatiquement le PDF
                const nomFichier = ExportService.genererNomFichierPDF(devisExistant.reference);
                ExportService.telechargerPDF(result.pdfUrl, nomFichier);

                // Mettre à jour le statut local si le backend l'a changé
                if (result.statut) {
                  setDevisExistant((prev: any) => ({ 
                    ...prev, 
                    statut: result.statut,
                    pdf_genere_le: new Date().toISOString()
                  }));
                }

                setConfirmationMessage(`PDF généré et téléchargé avec succès ! Statut mis à jour : ${result.statut || 'EN_ATTENTE_RETOUR_CLIENT'}`);
                setTimeout(() => setConfirmationMessage(null), 5000);

                // Nettoyer l'URL du blob après téléchargement
                setTimeout(() => {
                  if (result.pdfUrl) {
                    window.URL.revokeObjectURL(result.pdfUrl);
                  }
                }, 1000);
              } else {
                setError(result.error || 'Erreur lors de la génération du PDF');
              }
            } catch (error) {
              console.error('❌ Erreur génération PDF:', error);
              setError('Erreur lors de la génération du PDF');
            } finally {
              setLoading(false);
            }
          }}
          onMarquerLivre={() => {
            // TODO: Implémenter marquage comme livré selon la spec des statuts

          }}
          isEditMode={isEditMode}
          devisModifie={devisModifie}
        />
      )}

      {isEditMode && devisExistant?.id && (
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white shadow rounded-lg p-6 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notes internes (non visibles sur le devis)</h3>
                <p className="text-sm text-gray-600">
                  Codes d’accès ponton, consignes atelier, relances client, points à vérifier…
                </p>
              </div>
              {notesMessage && <span className="text-sm text-green-700">{notesMessage}</span>}
            </div>
            <textarea
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              rows={4}
              placeholder="Codes d’accès ponton, consignes atelier, relances client, points à vérifier…"
              value={notesInternes}
              onChange={(e) => setNotesInternes(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Jamais intégré au PDF client.</span>
              <button
                type="button"
                className="btn-primary"
                onClick={sauvegarderNotesInternes}
                disabled={notesSaving}
              >
                {notesSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>

          <DevisAttachmentsSection devisId={devisExistant.id} />
        </div>
      )}

      {/* Modale pour la finalisation du devis */}
      <FinalisationModal
        isOpen={showFinalisationModal}
        finalisationData={finalisationData}
        clientInfo={clientInfo}
        devisExistant={devisExistant}
        montantTotal={(() => {
          const total = calculerTotal();
          return total;
        })()}
        onClose={() => {
          setShowFinalisationModal(false);
          setFinalisationMode('full');
        }}
        soumisATVA={entrepriseData?.soumisATVA}
        tauxTVA={entrepriseData?.tauxTVA}
        viewMode={finalisationMode}
        onSave={async (data) => {

          // Mettre à jour les données de finalisation localement d'abord
          setFinalisationData(data);
          setDevisModifie(true);

          // Recharger le devis pour récupérer les données fraîches avec retry
          if (devisExistant?.id) {

            // Attendre un peu pour laisser le temps à la sauvegarde de se terminer
            await new Promise(resolve => setTimeout(resolve, 500));
            
            let tentatives = 0;
            const maxTentatives = 3;
            let rechargeReussie = false;
            
            while (tentatives < maxTentatives && !rechargeReussie) {
              tentatives++;

              try {
                const devisRecharge = await devisApiService.getDevisById(devisExistant.id);
                console.log('✅ [ConfigurateurArticle] Devis rechargé avec succès:', {
                  id: devisRecharge.id,
                  reference: devisRecharge.reference,
                  hasFinalisationData: !!(devisRecharge as any).finalisationData
                });
                
                setDevisExistant(devisRecharge);
                
                // Mettre à jour finalisationData avec les données du devis rechargé
                if ((devisRecharge as any).finalisationData) {

                  setFinalisationData((devisRecharge as any).finalisationData);
                } else {

                }
                
                rechargeReussie = true;

              } catch (retryError: any) {
                console.error(`❌ [ConfigurateurArticle] Tentative ${tentatives}/${maxTentatives} échouée:`, {
                  error: retryError.message,
                  type: retryError.name,
                  stack: retryError.stack?.split('\n').slice(0, 3).join('\n')
                });
                
                if (tentatives < maxTentatives) {
                  const delaiAttente = 1000 * tentatives;

                  await new Promise(resolve => setTimeout(resolve, delaiAttente));
                } else {
                  console.error('❌ [ConfigurateurArticle] Toutes les tentatives de rechargement ont échoué');
                  
                  // Afficher un message d'erreur clair à l'utilisateur
                  setError(`Erreur de synchronisation : Impossible de recharger le devis après la sauvegarde. ${retryError.message}`);
                  setConfirmationMessage('Données de finalisation sauvegardées (erreur de synchronisation)');
                  setTimeout(() => setConfirmationMessage(null), 5000);
                }
              }
            }
            
            if (rechargeReussie) {
              setConfirmationMessage('Données de finalisation sauvegardées et synchronisées');
              setTimeout(() => setConfirmationMessage(null), 3000);
            }
          } else {

            setConfirmationMessage('Données de finalisation sauvegardées');
            setTimeout(() => setConfirmationMessage(null), 3000);
          }

        }}
      />
    </div>
  );
};

export default ConfigurateurArticle;

